<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="legalization-page">
    <div class="page-header">
      <div class="page-header-text">
        <div class="page-eyebrow">Kadry</div>
        <h1 class="page-title">Legalizacja pobytu i pracy</h1>
      </div>
      <v-spacer></v-spacer>
      <v-btn color="primary" outlined :loading="loading" @click="odswiez">
        <v-icon left>mdi-refresh</v-icon>Odśwież
      </v-btn>
    </div>

    <v-tabs v-model="zakladka" class="mb-4" background-color="transparent">
      <v-tab>Kierowcy</v-tab>
      <v-tab>Sprawy</v-tab>
      <v-tab>Reguły</v-tab>
      <v-tab>Sprzedaż</v-tab>
      <v-tab>Klienci</v-tab>
      <v-tab>Abonamenty</v-tab>
      <v-tab>Zapytania</v-tab>
    </v-tabs>

    <v-tabs-items v-model="zakladka" style="background: transparent" touchless>
      <!-- ══ KIEROWCY ══ -->
      <v-tab-item>
        <!-- Kafelki: od najpilniejszego. Klik filtruje listę. -->
        <v-row dense class="mb-2">
          <v-col cols="6" md="3" v-for="k in kafelki" :key="k.status">
            <v-card
              outlined
              class="rounded-lg lg-tile"
              :class="{ 'lg-tile--active': status === k.status }"
              @click="toggleStatus(k.status)"
            >
              <v-card-text class="py-3">
                <div class="d-flex align-center">
                  <v-icon :color="k.kolor" class="mr-2">{{ k.ikona }}</v-icon>
                  <div>
                    <div class="text-h5 font-weight-bold">{{ summary[k.pole] || 0 }}</div>
                    <div class="caption grey--text">{{ k.tytul }}</div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-card outlined class="rounded-lg mb-4">
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="search"
                  label="Szukaj kierowcy"
                  prepend-inner-icon="mdi-magnify"
                  outlined dense hide-details clearable
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="citizenship"
                  :items="opcjeObywatelstw"
                  label="Obywatelstwo"
                  outlined dense hide-details clearable
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="status"
                  :items="opcjeStatusow"
                  label="Stan"
                  outlined dense hide-details clearable
                />
              </v-col>
              <v-col cols="12" md="2" class="d-flex align-center">
                <v-btn text small @click="wyczyscFiltry">Wyczyść filtry</v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card outlined class="rounded-lg">
          <v-data-table
            :headers="headers"
            :items="widoczne"
            :loading="loading"
            :items-per-page="25"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
            no-data-text="Brak kierowców wymagających legalizacji."
            @click:row="otworzKierowce"
          >
            <template v-slot:item.name="{ item }">
              <div class="font-weight-medium">{{ item.name }}</div>
              <div class="caption grey--text">#{{ item.numericId }}</div>
            </template>

            <template v-slot:item.citizenshipName="{ item }">
              <span v-if="item.citizenshipName">{{ item.citizenshipName }}</span>
              <v-tooltip bottom v-else>
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="orange--text">
                    <v-icon small color="orange">mdi-help-circle-outline</v-icon>
                    nieustalone
                  </span>
                </template>
                <span>Wpisane przy rejestracji: „{{ item.citizenshipText || 'brak' }}"</span>
              </v-tooltip>
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip small :color="kolorStanu(item.status)" text-color="white">
                {{ etykietaStanu(item.status) }}
              </v-chip>
            </template>

            <template v-slot:item.residenceUntil="{ item }">
              <span v-if="item.residenceUntil">
                {{ formatDate(item.residenceUntil) }}
                <span class="caption" :class="klasaDni(item.residenceDays)">
                  ({{ opisDni(item.residenceDays) }})
                </span>
              </span>
              <span v-else class="grey--text">—</span>
            </template>

            <template v-slot:item.workUntil="{ item }">
              <span v-if="item.workUntil">
                {{ formatDate(item.workUntil) }}
                <span class="caption" :class="klasaDni(item.workDays)">
                  ({{ opisDni(item.workDays) }})
                </span>
              </span>
              <span v-else class="grey--text">—</span>
            </template>

            <template v-slot:item.braki="{ item }">
              <span v-if="!item.braki.length" class="grey--text">—</span>
              <v-tooltip bottom v-else>
                <template v-slot:activator="{ on }">
                  <v-chip small outlined color="orange" v-on="on">
                    {{ item.braki.length }} {{ item.braki.length === 1 ? 'brak' : 'braki' }}
                  </v-chip>
                </template>
                <span>{{ item.braki.join(', ') }}</span>
              </v-tooltip>
            </template>

            <template v-slot:item.verifiedAt="{ item }">
              <v-icon v-if="item.verifiedAt" small color="success">mdi-check-decagram</v-icon>
              <span v-else class="caption grey--text">niesprawdzone</span>
            </template>

            <!-- Przy kierowcy bez ustalonego obywatelstwa liczy się jedno kliknięcie:
                 takich osób jest w systemie więcej niż spraw legalizacyjnych. -->
            <template v-slot:item.akcje="{ item }">
              <div v-if="!item.citizenshipCode" @click.stop>
                <v-btn x-small text color="primary" @click="szybkoPolska(item)">Polska</v-btn>
                <v-btn x-small text @click="otworzUstalanie(item)">inne…</v-btn>
              </div>
              <v-icon v-else small color="grey lighten-1">mdi-chevron-right</v-icon>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- ══ SPRAWY ══ -->
      <v-tab-item>
        <v-card outlined class="rounded-lg mb-4">
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="szukajSprawy"
                  label="Szukaj kierowcy"
                  prepend-inner-icon="mdi-magnify"
                  outlined dense hide-details clearable
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filtrStatusSprawy"
                  :items="statusySprawy"
                  item-text="nazwa"
                  item-value="kod"
                  label="Status sprawy"
                  outlined dense hide-details clearable
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filtrProcedura"
                  :items="procedury"
                  item-text="nazwa"
                  item-value="kod"
                  label="Rodzaj sprawy"
                  outlined dense hide-details clearable
                />
              </v-col>
              <v-col cols="12" md="2" class="d-flex align-center justify-end">
                <v-btn color="primary" small :disabled="!canEdit" @click="otworzKreator">
                  <v-icon left small>mdi-plus</v-icon>Nowa sprawa
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card outlined class="rounded-lg">
          <v-data-table
            :headers="naglowkiSpraw"
            :items="widoczneSprawy"
            :loading="ladowanieSpraw"
            :items-per-page="25"
            no-data-text="Nie ma jeszcze żadnej sprawy."
            @click:row="(s) => otworzSprawe(s.id)"
          >
            <template v-slot:item.driverName="{ item }">
              <div class="font-weight-medium">{{ item.driverName }}</div>
              <div class="caption grey--text">
                {{ item.citizenshipName || 'obywatelstwo nieustalone' }}
              </div>
            </template>
            <template v-slot:item.statusNazwa="{ item }">
              <v-chip small :color="kolorSprawy(item.status)" text-color="white">
                {{ item.statusNazwa }}
              </v-chip>
            </template>
            <template v-slot:item.kompletnosc="{ item }">
              <div class="d-flex align-center">
                <v-progress-linear
                  :value="item.kompletnosc.procent"
                  height="6"
                  rounded
                  :color="item.kompletnosc.mozeByc100 ? 'success' : 'orange'"
                  style="width: 70px"
                  class="mr-2"
                />
                <span class="caption">{{ item.kompletnosc.procent }}%</span>
              </div>
              <div v-if="item.ostrzezenia && item.ostrzezenia.length" class="caption orange--text">
                {{ item.ostrzezenia.length }} ostrzeżeń
              </div>
            </template>
            <template v-slot:item.createdAt="{ item }">
              {{ formatDate(item.createdAt) }}
            </template>
            <template v-slot:item.deadlineAt="{ item }">
              <span v-if="item.deadlineAt">{{ formatDate(item.deadlineAt) }}</span>
              <span v-else class="grey--text">—</span>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- ══ REGUŁY ══ -->
      <v-tab-item>
        <v-alert type="info" text dense class="mb-3">
          Reguły decydują o tym, jakich dokumentów system zażąda od konkretnej osoby.
          Zestaw działa dopiero po zatwierdzeniu przez człowieka, a zatwierdzić da się
          tylko taki, który wskazuje oficjalne źródło i datę jego sprawdzenia.
        </v-alert>

        <div class="d-flex mb-3">
          <v-spacer />
          <v-btn small outlined :loading="wgrywanie" :disabled="!canEdit" @click="wgrajStartowe">
            <v-icon left small>mdi-download</v-icon>Wgraj zestawy startowe
          </v-btn>
        </div>

        <v-card outlined class="rounded-lg">
          <v-data-table
            :headers="naglowkiRegul"
            :items="zestawy"
            :loading="ladowanieRegul"
            :items-per-page="25"
            no-data-text="Brak zestawów reguł. Wgraj startowe albo dodaj własny."
          >
            <template v-slot:item.nazwa="{ item }">
              <div class="font-weight-medium">{{ item.nazwa }}</div>
              <div class="caption grey--text">
                {{ item.proceduraNazwa }} · {{ item.liczbaRegul }} reguł
                <span v-if="item.obywatelstwa && item.obywatelstwa.length">
                  · {{ item.obywatelstwa.join(', ') }}
                </span>
                <span v-if="item.urzad"> · {{ item.urzad }}</span>
              </div>
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip small :color="kolorZestawu(item.status)" text-color="white">
                {{ etykietaZestawu(item.status) }}
              </v-chip>
              <span class="caption grey--text ml-1">w. {{ item.wersja }}</span>
            </template>
            <template v-slot:item.zweryfikowanoDnia="{ item }">
              <span v-if="item.zweryfikowanoDnia">
                {{ formatDate(item.zweryfikowanoDnia) }}
                <div v-if="item.wymagaPonownejWeryfikacji" class="caption orange--text">
                  wymaga sprawdzenia
                </div>
              </span>
              <span v-else class="orange--text caption">nie sprawdzono</span>
            </template>
            <template v-slot:item.zrodloUrl="{ item }">
              <a v-if="item.zrodloUrl" :href="item.zrodloUrl" target="_blank" rel="noopener" class="caption">
                otwórz źródło
              </a>
              <span v-else class="caption error--text">brak źródła</span>
            </template>
            <template v-slot:item.akcje="{ item }">
              <v-btn
                v-if="item.status !== 'ACTIVE'"
                x-small text color="success"
                :disabled="!canEdit"
                @click="zatwierdzZestaw(item)"
              >Zatwierdź</v-btn>
              <v-btn
                v-if="item.status === 'ACTIVE'"
                x-small text color="grey"
                :disabled="!canEdit"
                @click="archiwizujZestaw(item)"
              >Archiwizuj</v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>
      <!-- ══ SPRZEDAŻ ══ -->
      <v-tab-item>
        <v-row dense class="mb-3">
          <v-col cols="6" md="3">
            <v-card outlined class="rounded-lg">
              <v-card-text class="py-3">
                <div class="text-h6 font-weight-bold">{{ zl(raport.przychodBruttoGr) }}</div>
                <div class="caption grey--text">Przychód brutto (opłacone)</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined class="rounded-lg">
              <v-card-text class="py-3">
                <div class="text-h6 font-weight-bold orange--text">{{ zl(raport.naleznosciBruttoGr) }}</div>
                <div class="caption grey--text">Do zapłaty przez klientów</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined class="rounded-lg">
              <v-card-text class="py-3">
                <div class="text-h6 font-weight-bold">{{ raport.zamowien || 0 }}</div>
                <div class="caption grey--text">
                  zamówień, w tym {{ raport.oplaconych || 0 }} opłaconych
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined class="rounded-lg">
              <v-card-text class="py-3">
                <div class="text-h6 font-weight-bold grey--text">{{ zl(raport.oplatyZewnetrzneGr) }}</div>
                <div class="caption grey--text">Opłaty urzędowe — nie nasz przychód</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Co z tego zostaje w firmie -->
        <v-row dense class="mb-4">
          <v-col cols="6" md="3">
            <v-card outlined class="rounded-lg">
              <v-card-text class="py-3">
                <div class="text-h6 font-weight-bold success--text">{{ zl(raport.marzaGr) }}</div>
                <div class="caption grey--text">
                  Marża na czysto
                  <span v-if="raport.marzaProcent !== null && raport.marzaProcent !== undefined">
                    · {{ raport.marzaProcent }}% przychodu
                  </span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined class="rounded-lg">
              <v-card-text class="py-3">
                <div class="text-h6 font-weight-bold">{{ zl(raport.kosztyWlasneGr) }}</div>
                <div class="caption grey--text">Opłaty wyłożone przez nas</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined class="rounded-lg">
              <v-card-text class="py-3">
                <div v-if="(raport.praca || {}).marzaNaGodzineGr" class="text-h6 font-weight-bold">
                  {{ zl(raport.praca.marzaNaGodzineGr) }}/h
                </div>
                <div v-else class="text-h6 font-weight-bold grey--text">—</div>
                <div class="caption grey--text">
                  <span v-if="(raport.praca || {}).uwaga">{{ raport.praca.uwaga }}</span>
                  <span v-else>Marża na godzinę pracy ({{ (raport.praca || {}).godzin || 0 }} h)</span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined class="rounded-lg">
              <v-card-text class="py-3">
                <div class="text-h6 font-weight-bold">{{ (raport.faktury || {}).wystawionych || 0 }}</div>
                <div class="caption grey--text">
                  faktur, {{ (raport.faktury || {}).wKsef || 0 }} w KSeF
                  <span v-if="(raport.faktury || {}).bezFaktury" class="orange--text">
                    · {{ raport.faktury.bezFaktury }} opłaconych bez faktury
                  </span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Cennik -->
        <v-card outlined class="rounded-lg mb-4">
          <v-card-title class="subtitle-1">
            <v-icon left small>mdi-tag-multiple-outline</v-icon>Pakiety i ceny
            <v-spacer />
            <v-btn small color="primary" :disabled="!canEdit" @click="otworzPakiet(null)">
              <v-icon left small>mdi-plus</v-icon>Nowy pakiet
            </v-btn>
          </v-card-title>
          <v-card-text>
            <p v-if="!pakiety.length" class="grey--text mb-0">
              Nie ma jeszcze żadnego pakietu. Bez pakietu z ceną nie da się sprzedać usługi.
            </p>
            <v-simple-table v-else dense>
              <thead>
                <tr>
                  <th>Pakiet</th>
                  <th>Zakres</th>
                  <th>Cena standardowa</th>
                  <th>Cena dla kierowcy BTP</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in pakiety" :key="p.id">
                  <td>
                    <div class="font-weight-medium">{{ p.nazwa }}</div>
                    <div class="caption grey--text">
                      {{ p.kod }} · VAT {{ p.vatRate }}%
                      <span v-if="!p.aktywny" class="error--text">· wyłączony</span>
                    </div>
                  </td>
                  <td class="caption" style="max-width: 280px">
                    {{ (p.zakres || []).join(' · ') || '—' }}
                  </td>
                  <td>
                    <span v-if="cenaZ(p, 'STANDARD')">{{ zl(cenaZ(p, 'STANDARD')) }}</span>
                    <span v-else class="error--text caption">brak ceny</span>
                    <v-btn x-small text color="primary" :disabled="!canEdit"
                      @click="otworzCene(p, 'STANDARD')">zmień</v-btn>
                  </td>
                  <td>
                    <span v-if="cenaZ(p, 'KIEROWCA_BTP')">{{ zl(cenaZ(p, 'KIEROWCA_BTP')) }}</span>
                    <span v-else class="caption grey--text">jak standardowa</span>
                    <v-btn x-small text color="primary" :disabled="!canEdit"
                      @click="otworzCene(p, 'KIEROWCA_BTP')">zmień</v-btn>
                  </td>
                  <td class="text-right">
                    <v-btn x-small text :disabled="!canEdit" @click="otworzPakiet(p)">edytuj</v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <p class="caption grey--text mt-2 mb-0">
              Zmiana ceny nie rusza spraw już sprzedanych — każde zamówienie trzyma cenę
              z chwili zakupu.
            </p>
          </v-card-text>
        </v-card>

        <!-- Zamówienia -->
        <v-card outlined class="rounded-lg">
          <v-card-title class="subtitle-1">
            <v-icon left small>mdi-cart-outline</v-icon>Zamówienia
            <v-spacer />
            <v-select
              v-model="filtrPlatnosci"
              :items="statusyPlatnosci"
              item-text="nazwa"
              item-value="kod"
              label="Status płatności"
              outlined dense hide-details clearable
              style="max-width: 240px"
              @change="pobierzZamowienia"
            />
          </v-card-title>
          <v-data-table
            :headers="naglowkiZamowien"
            :items="zamowienia"
            :loading="ladowanieSprzedazy"
            :items-per-page="25"
            no-data-text="Nie ma jeszcze żadnego zamówienia."
          >
            <template v-slot:item.klient="{ item }">
              <div v-if="item.driver" class="font-weight-medium">
                {{ item.driver.firstName }} {{ item.driver.lastName }}
                <span class="caption grey--text">· kierowca BTP</span>
              </div>
              <span v-else class="grey--text">klient zewnętrzny</span>
            </template>
            <template v-slot:item.pakietNazwa="{ item }">
              <div>{{ item.pakietNazwa }}</div>
              <div class="caption grey--text">
                cennik: {{ item.cennikNazwa === 'KIEROWCA_BTP' ? 'kierowca BTP' : 'standardowy' }}
              </div>
            </template>
            <template v-slot:item.bruttoGr="{ item }">
              <div class="font-weight-medium">{{ zl(item.bruttoGr) }}</div>
              <div v-if="item.podsumowanie.oplatyZewnetrzneKlientGr" class="caption grey--text">
                + {{ zl(item.podsumowanie.oplatyZewnetrzneKlientGr) }} opłat urzędowych
              </div>
            </template>
            <template v-slot:item.statusPlatnosciNazwa="{ item }">
              <v-chip small :color="kolorPlatnosci(item.statusPlatnosci)" text-color="white">
                {{ item.statusPlatnosciNazwa }}
              </v-chip>
            </template>
            <template v-slot:item.faktura="{ item }">
              <div v-if="item.faktura">
                <a class="font-weight-medium" @click.prevent="otworzPdf(item.faktura.id)">
                  {{ item.faktura.number }}
                </a>
                <div class="caption">
                  <span v-if="item.faktura.emailSentAt" class="success--text">wysłana mailem</span>
                  <span v-else-if="item.faktura.buyerEmail" class="orange--text">
                    niewysłana<span v-if="item.faktura.emailError"> ({{ item.faktura.emailError }})</span>
                  </span>
                  <span v-else class="grey--text">brak maila nabywcy</span>
                  ·
                  <span v-if="item.faktura.ksefStatus === 'SENT'" class="success--text">w KSeF</span>
                  <span v-else-if="item.faktura.ksefStatus === 'ERROR'" class="error--text">KSeF: błąd</span>
                  <span v-else class="grey--text">nie w KSeF</span>
                </div>
              </div>
              <span v-else class="caption grey--text">—</span>
            </template>
            <template v-slot:item.createdAt="{ item }">{{ formatDate(item.createdAt) }}</template>
            <template v-slot:item.akcje="{ item }">
              <v-btn
                v-if="!item.podsumowanie.oplacone && item.statusPlatnosci !== 'ANULOWANE'"
                x-small color="success" :disabled="!canEdit" class="mr-1" @click="otworzZaplate(item)"
              >Oznacz zapłatę</v-btn>
              <v-btn
                v-if="!item.invoiceId && item.statusPlatnosci !== 'ANULOWANE'"
                x-small color="primary" outlined :disabled="!canEdit" class="mr-1"
                @click="otworzFakture(item)"
              >Wystaw fakturę</v-btn>
              <template v-else-if="item.faktura">
                <v-btn x-small text class="mr-1" @click="otworzPdf(item.faktura.id)">PDF</v-btn>
                <v-btn
                  v-if="item.faktura.buyerEmail"
                  x-small text :disabled="!canEdit" :loading="wysylka === item.faktura.id" class="mr-1"
                  @click="wyslijFakture(item.faktura, !!item.faktura.emailSentAt)"
                >{{ item.faktura.emailSentAt ? 'Wyślij ponownie' : 'Wyślij mailem' }}</v-btn>
                <v-btn
                  v-if="item.faktura.ksefStatus !== 'SENT'"
                  x-small text color="deep-purple" :disabled="!canEdit" :loading="ksefTrwa === item.faktura.id"
                  class="mr-1" @click="doKsef(item.faktura)"
                >Do KSeF</v-btn>
                <v-btn
                  v-if="item.faktura.status !== 'PAID'"
                  x-small text color="success" :disabled="!canEdit" @click="fakturaZaplacona(item.faktura)"
                >Faktura zapłacona</v-btn>
              </template>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- ══ KLIENCI ══ -->
      <v-tab-item>
        <v-card outlined class="rounded-lg">
          <v-card-title class="subtitle-1">
            <v-icon left small>mdi-domain</v-icon>Klienci spoza BTP
            <v-spacer />
            <v-text-field
              v-model="szukajKlienta" label="Szukaj: nazwa, NIP, e-mail"
              outlined dense hide-details clearable style="max-width: 300px" class="mr-2"
              @input="pobierzKlientowZOpoznieniem"
            />
            <v-btn small color="primary" :disabled="!canEdit" @click="otworzKlienta(null)">
              <v-icon left small>mdi-plus</v-icon>Nowy klient
            </v-btn>
          </v-card-title>
          <v-card-text v-if="!klienci.length && !ladowanieKlientow" class="grey--text">
            Nie ma jeszcze żadnego klienta zewnętrznego. Kierowcy BTP kupują usługę na swojej
            karcie — tutaj wpisuje się firmy i osoby z zewnątrz.
          </v-card-text>
          <v-data-table
            v-else
            :headers="naglowkiKlientow"
            :items="klienci"
            :loading="ladowanieKlientow"
            :items-per-page="25"
          >
            <template v-slot:item.nazwa="{ item }">
              <a class="font-weight-medium" @click.prevent="otworzKarte(item)">{{ item.nazwa }}</a>
              <div class="caption grey--text">
                {{ item.typ === 'B2B' ? 'firma' : 'osoba prywatna' }}
                <span v-if="item.nip">· NIP {{ item.nip }}</span>
                <span v-if="!item.aktywny" class="error--text">· wyłączony</span>
              </div>
            </template>
            <template v-slot:item.kontakt="{ item }">
              <div v-if="item.email" class="caption">{{ item.email }}</div>
              <div v-if="item.telefon" class="caption grey--text">{{ item.telefon }}</div>
              <div v-if="item.osobaKontaktowa" class="caption grey--text">{{ item.osobaKontaktowa }}</div>
              <span v-if="!item.email && !item.telefon" class="caption error--text">brak kontaktu</span>
            </template>
            <template v-slot:item.warunki="{ item }">
              <div class="caption">
                <span v-if="item.maWlasnyCennik" class="success--text">własny cennik</span>
                <span v-else class="grey--text">cennik standardowy</span>
              </div>
              <div class="caption">
                <span v-if="item.rabatProcent">rabat {{ item.rabatProcent }}%</span>
                <span v-else class="grey--text">bez rabatu</span>
                · {{ item.terminPlatnosciDni }} dni na zapłatę
              </div>
            </template>
            <template v-slot:item.sprzedaz="{ item }">
              <div v-if="item.sprzedaz.zamowien" class="font-weight-medium">
                {{ zl(item.sprzedaz.bruttoGr) }}
              </div>
              <div class="caption grey--text">
                {{ item.sprzedaz.zamowien || 0 }} zamówień
              </div>
            </template>
            <template v-slot:item.opiekun="{ item }">
              <span v-if="item.opiekun" class="caption">{{ item.opiekun }}</span>
              <span v-else class="caption grey--text">—</span>
            </template>
            <template v-slot:item.akcje="{ item }">
              <v-btn x-small text color="primary" class="mr-1" @click="otworzKarte(item)">Otwórz</v-btn>
              <v-btn x-small text :disabled="!canEdit" class="mr-1" @click="otworzKlienta(item)">Edytuj</v-btn>
              <v-btn x-small text color="error" :disabled="!canEdit" @click="wylaczKlienta(item)">Wyłącz</v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- ══ ABONAMENTY ══ -->
      <v-tab-item>
        <!-- Ile firma zarabia miesięcznie i ile jej wiszą -->
        <v-row dense class="mb-4">
          <v-col cols="6" md="3">
            <v-card outlined class="rounded-lg">
              <v-card-text class="py-3">
                <div class="text-h6 font-weight-bold success--text">{{ zl(raportAb.mrrBruttoGr) }}</div>
                <div class="caption grey--text">
                  Przychód miesięczny (przy dzisiejszym stanie)
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined class="rounded-lg">
              <v-card-text class="py-3">
                <div class="text-h6 font-weight-bold">{{ raportAb.osobPodOpieka || 0 }}</div>
                <div class="caption grey--text">
                  osób pod opieką w {{ raportAb.aktywnych || 0 }} abonamentach
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined class="rounded-lg">
              <v-card-text class="py-3">
                <div class="text-h6 font-weight-bold orange--text">
                  {{ zl((raportAb.doRozliczenia || {}).bruttoGr) }}
                </div>
                <div class="caption grey--text">
                  do zafakturowania ({{ (raportAb.doRozliczenia || {}).okresow || 0 }} okresów)
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined class="rounded-lg">
              <v-card-text class="py-3">
                <div class="text-h6 font-weight-bold">{{ zl((raportAb.naleznosci || {}).bruttoGr) }}</div>
                <div class="caption grey--text">
                  niezapłacone
                  <span v-if="(raportAb.naleznosci || {}).przeterminowanych" class="error--text">
                    · {{ raportAb.naleznosci.przeterminowanych }} po terminie
                    ({{ zl(raportAb.naleznosci.przeterminowaneBruttoGr) }})
                  </span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-alert v-if="(raportAb.puste || []).length" type="warning" text dense class="mb-4">
          <strong>{{ raportAb.puste.length }}</strong> aktywnych abonamentów nie ma ani jednej osoby
          pod opieką — nie ma za co wystawić opłaty:
          {{ raportAb.puste.map((p) => p.nazwa + ' (' + p.nabywca + ')').join(', ') }}
        </v-alert>

        <!-- Abonamenty -->
        <v-card outlined class="rounded-lg mb-4">
          <v-card-title class="subtitle-1">
            <v-icon left small>mdi-calendar-sync-outline</v-icon>Abonamenty monitoringu
            <v-spacer />
            <v-btn small color="primary" :disabled="!canEdit" @click="otworzAbonament(null)">
              <v-icon left small>mdi-plus</v-icon>Nowy abonament
            </v-btn>
          </v-card-title>
          <v-card-text v-if="!abonamenty.length && !ladowanieAbonamentow" class="grey--text">
            Nie ma jeszcze żadnego abonamentu. Abonament to stała opłata za osobę miesięcznie
            za pilnowanie jej terminów — po zakończonej sprawie ktoś musi pamiętać, że karta
            pobytu wygaśnie za dwa lata.
          </v-card-text>
          <v-data-table
            v-else
            :headers="naglowkiAbonamentow"
            :items="abonamenty"
            :loading="ladowanieAbonamentow"
            :items-per-page="25"
          >
            <template v-slot:item.nazwa="{ item }">
              <a class="font-weight-medium" @click.prevent="otworzKarteAbonamentu(item)">{{ item.nazwa }}</a>
              <div class="caption grey--text">{{ item.nabywca }}</div>
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip x-small :color="kolorAbonamentu(item.status)" text-color="white">
                {{ item.statusNazwa }}
              </v-chip>
            </template>
            <template v-slot:item.osoby="{ item }">
              <div class="font-weight-medium">{{ item.osobCzynnych }}</div>
              <div class="caption grey--text">× {{ zl(item.cenaOsobaGr) }}/mies.</div>
            </template>
            <template v-slot:item.miesiecznie="{ item }">
              <span class="font-weight-medium">{{ zl(item.miesiecznieBruttoGr) }}</span>
            </template>
            <template v-slot:item.rozliczenie="{ item }">
              <div class="caption">{{ item.dzienRozliczenia }}. dnia miesiąca</div>
              <div v-if="item.niezaplaconeBruttoGr" class="caption orange--text">
                niezapłacone: {{ zl(item.niezaplaconeBruttoGr) }}
              </div>
            </template>
            <template v-slot:item.akcje="{ item }">
              <v-btn x-small text color="primary" class="mr-1"
                @click="otworzKarteAbonamentu(item)">Otwórz</v-btn>
              <v-btn x-small text :disabled="!canEdit" class="mr-1"
                @click="otworzAbonament(item)">Edytuj</v-btn>
              <v-btn
                v-if="item.status === 'AKTYWNY'"
                x-small text color="success" :disabled="!canEdit || !item.osobCzynnych"
                :loading="domykanie === item.id" @click="domknijOkres(item)"
              >Rozlicz miesiąc</v-btn>
            </template>
          </v-data-table>
        </v-card>

        <!-- Okresy do rozliczenia i należności -->
        <v-card outlined class="rounded-lg">
          <v-card-title class="subtitle-1">
            <v-icon left small>mdi-file-clock-outline</v-icon>Okresy rozliczeniowe
            <v-spacer />
            <v-select
              v-model="filtrOkresu" :items="statusyOkresu" item-text="nazwa" item-value="kod"
              label="Status" outlined dense hide-details clearable style="max-width: 220px"
              @change="pobierzOkresy"
            />
          </v-card-title>
          <v-data-table
            :headers="naglowkiOkresow"
            :items="okresy"
            :loading="ladowanieAbonamentow"
            :items-per-page="25"
            no-data-text="Nie ma żadnych okresów — rozlicz miesiąc albo poczekaj na cron o 6:30."
          >
            <template v-slot:item.klucz="{ item }">
              <div class="font-weight-medium">{{ item.klucz }}</div>
              <div class="caption grey--text">{{ item.abonament.nazwa }}</div>
            </template>
            <template v-slot:item.nabywca="{ item }">{{ item.nabywca }}</template>
            <template v-slot:item.osob="{ item }">
              <div class="font-weight-medium">{{ item.osob }}</div>
              <div class="caption grey--text">× {{ zl(item.cenaOsobaGr) }}</div>
            </template>
            <template v-slot:item.bruttoGr="{ item }">{{ zl(item.bruttoGr) }}</template>
            <template v-slot:item.status="{ item }">
              <v-chip x-small :color="kolorOkresu(item.status)" text-color="white">
                {{ item.statusNazwa }}
              </v-chip>
              <div v-if="item.dniPoTerminie !== null" class="caption error--text">
                {{ item.dniPoTerminie }} dni po terminie
              </div>
            </template>
            <template v-slot:item.faktura="{ item }">
              <a v-if="item.faktura" @click.prevent="otworzPdf(item.faktura.id)">
                {{ item.faktura.number }}
              </a>
              <span v-else class="caption grey--text">—</span>
            </template>
            <template v-slot:item.akcje="{ item }">
              <v-btn
                v-if="item.status === 'DO_ROZLICZENIA'"
                x-small color="primary" outlined :disabled="!canEdit"
                :loading="fakturaOkresu === item.id" @click="fakturaZaOkres(item)"
              >Wystaw fakturę</v-btn>
              <v-btn
                v-else-if="item.status === 'ZAFAKTUROWANY' && item.faktura"
                x-small text color="success" :disabled="!canEdit"
                @click="fakturaZaplacona(item.faktura)"
              >Zapłacone</v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- ══ ZAPYTANIA I POLECENIA ══ -->
      <v-tab-item>
        <v-row dense class="mb-4">
          <v-col cols="6" md="3">
            <v-card outlined class="rounded-lg">
              <v-card-text class="py-3">
                <div class="text-h6 font-weight-bold">{{ raportLeadow.zgloszen || 0 }}</div>
                <div class="caption grey--text">
                  zapytań (90 dni)
                  <span v-if="raportLeadow.spam" class="grey--text">· {{ raportLeadow.spam }} spam</span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined class="rounded-lg">
              <v-card-text class="py-3">
                <div class="text-h6 font-weight-bold success--text">{{ raportLeadow.wygranych || 0 }}</div>
                <div class="caption grey--text">
                  wygranych
                  <span v-if="raportLeadow.skutecznoscProcent !== null">
                    · {{ raportLeadow.skutecznoscProcent }}% skuteczności
                  </span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined class="rounded-lg">
              <v-card-text class="py-3">
                <div class="text-h6 font-weight-bold orange--text">{{ raportLeadow.wOfercie || 0 }}</div>
                <div class="caption grey--text">z ofertą, czeka na decyzję</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined class="rounded-lg">
              <v-card-text class="py-3">
                <div class="text-h6 font-weight-bold">
                  {{ zl((raportLeadow.nagrodyZaPolecenia || {}).razemGr) }}
                </div>
                <div class="caption grey--text">
                  nagrody za polecenia
                  <span v-if="(raportLeadow.nagrodyZaPolecenia || {}).doWyplatyGr" class="orange--text">
                    · {{ zl(raportLeadow.nagrodyZaPolecenia.doWyplatyGr) }} do wypłaty
                  </span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-alert v-if="(raportLeadow.zapomniane || []).length" type="warning" text dense class="mb-4">
          <strong>{{ raportLeadow.zapomniane.length }}</strong> zapytań czeka bez kontaktu ponad
          tydzień — lead, który czeka tydzień, jest leadem straconym:
          {{ raportLeadow.zapomniane.slice(0, 6).map((z) => z.nazwa + " (" + z.dni + " dni)").join(", ") }}
        </v-alert>

        <v-card outlined class="rounded-lg mb-4">
          <v-card-title class="subtitle-1">
            <v-icon left small>mdi-account-question-outline</v-icon>Zapytania
            <v-spacer />
            <v-select
              v-model="filtrLeada" :items="statusyLeada" item-text="nazwa" item-value="kod"
              label="Status" outlined dense hide-details clearable style="max-width: 200px" class="mr-2"
              @change="pobierzLeady"
            />
            <v-text-field
              v-model="szukajLeada" label="Szukaj" outlined dense hide-details clearable
              style="max-width: 220px" @input="pobierzLeadyZOpoznieniem"
            />
          </v-card-title>
          <v-card-text v-if="!leady.length && !ladowanieLeadow" class="grey--text">
            Nie ma jeszcze żadnego zapytania. Wpadają tu zgłoszenia z formularza na stronie
            oraz to, co biuro przekaże z formularza kontaktowego.
          </v-card-text>
          <v-data-table
            v-else
            :headers="naglowkiLeadow"
            :items="leady"
            :loading="ladowanieLeadow"
            :items-per-page="25"
          >
            <template v-slot:item.nazwa="{ item }">
              <div class="font-weight-medium">{{ item.nazwa }}</div>
              <div class="caption grey--text">
                {{ item.email || "—" }}<span v-if="item.telefon"> · {{ item.telefon }}</span>
              </div>
            </template>
            <template v-slot:item.sprawa="{ item }">
              <div class="caption">
                {{ item.procedura || "nie podano" }}
                <span v-if="item.osob > 1">· {{ item.osob }} osób</span>
              </div>
              <div class="caption grey--text">
                {{ item.zrodloNazwa }}
                <span v-if="item.polecajacy">· {{ item.polecajacy.nazwa }} ({{ item.kodPolecenia }})</span>
                <span v-else-if="item.kodPolecenia" class="error--text">
                  · kod „{{ item.kodPolecenia }}" nie pasuje do żadnego partnera
                </span>
              </div>
            </template>
            <template v-slot:item.oferta="{ item }">
              <div v-if="item.ofertaBruttoGr">
                <span class="font-weight-medium">{{ zl(item.ofertaBruttoGr) }}</span>
                <span v-if="item.osob > 1" class="caption grey--text">
                  (razem {{ zl(item.razemBruttoGr) }})
                </span>
                <div class="caption" :class="item.ofertaWygasla ? 'error--text' : 'grey--text'">
                  {{ item.ofertaWygasla ? "wygasła" : "ważna do" }} {{ formatDate(item.ofertaWaznaDo) }}
                </div>
              </div>
              <span v-else class="caption grey--text">bez wyceny</span>
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip x-small :color="kolorLeada(item.status)" text-color="white">
                {{ item.statusNazwa }}
              </v-chip>
              <div v-if="item.dniBezKontaktu !== null" class="caption"
                :class="item.dniBezKontaktu > 7 ? 'error--text' : 'grey--text'">
                {{ item.dniBezKontaktu }} dni bez kontaktu
              </div>
            </template>
            <template v-slot:item.akcje="{ item }">
              <v-btn x-small text color="primary" class="mr-1" @click="otworzLeada(item)">Otwórz</v-btn>
              <v-btn
                v-if="!['WYGRANY', 'SPAM'].includes(item.status)"
                x-small text :disabled="!canEdit" :loading="ofertaLeada === item.id"
                class="mr-1" @click="wyslijOferte(item)"
              >{{ item.ofertaBruttoGr ? "Nowa oferta" : "Wyceń i wyślij" }}</v-btn>
              <v-btn
                v-if="!item.clientId && item.status !== 'SPAM'"
                x-small color="success" outlined :disabled="!canEdit"
                @click="otworzKonwersje(item)"
              >Zamień na klienta</v-btn>
            </template>
          </v-data-table>
        </v-card>

        <v-card outlined class="rounded-lg">
          <v-card-title class="subtitle-1">
            <v-icon left small>mdi-handshake-outline</v-icon>Partnerzy polecający
            <v-spacer />
            <v-btn small color="primary" :disabled="!canEdit" @click="otworzPartnera(null)">
              <v-icon left small>mdi-plus</v-icon>Nowy partner
            </v-btn>
          </v-card-title>
          <v-card-text v-if="!partnerzy.length" class="grey--text">
            Nie ma partnerów. Partner dostaje własny kod; klient podaje go w formularzu,
            a nagroda nalicza się dopiero wtedy, gdy klient zapłaci.
          </v-card-text>
          <v-simple-table v-else dense>
            <thead>
              <tr>
                <th>Partner</th><th>Kod</th><th>Nagroda</th><th>Zapytania</th>
                <th>Do wypłaty</th><th>Wypłacone</th><th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in partnerzy" :key="p.id">
                <td>
                  <div class="font-weight-medium">{{ p.nazwa }}</div>
                  <div class="caption grey--text">
                    <span v-if="p.kto">{{ p.kto }}</span>
                    <span v-if="p.driverId"> · kierowca BTP</span>
                    <span v-if="!p.aktywny" class="error--text"> · wyłączony</span>
                  </div>
                </td>
                <td><code>{{ p.kod }}</code></td>
                <td>{{ zl(p.nagrodaGr) }}</td>
                <td>{{ p.zgloszen }}</td>
                <td>
                  <span v-if="p.doWyplatyGr" class="orange--text font-weight-medium">
                    {{ zl(p.doWyplatyGr) }}
                  </span>
                  <span v-else class="grey--text">—</span>
                </td>
                <td class="caption">{{ zl(p.wyplaconoGr) }} ({{ p.wyplaconoSzt }})</td>
                <td class="text-right">
                  <v-btn x-small text :disabled="!canEdit" @click="otworzPartnera(p)">edytuj</v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-card-text v-if="nagrodyDoWyplaty.length">
            <div class="subtitle-2 mb-1">Nagrody czekające na wypłatę</div>
            <v-simple-table dense>
              <tbody>
                <tr v-for="n in nagrodyDoWyplaty" :key="n.id">
                  <td>{{ n.polecajacy.nazwa }}</td>
                  <td class="caption">{{ n.lead ? n.lead.nazwa : n.zamowienie.pakietNazwa }}</td>
                  <td class="font-weight-medium">{{ zl(n.kwotaGr) }}</td>
                  <td class="text-right">
                    <v-btn x-small color="success" outlined :disabled="!canEdit"
                      @click="nagrodaWyplacona(n)">Wypłacone</v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <!-- Zapytanie: karta -->
    <v-dialog v-model="dialogLeada" max-width="720">
      <v-card v-if="lead">
        <v-card-title class="subtitle-1">
          {{ lead.nazwa }}
          <v-chip x-small :color="kolorLeada(lead.status)" text-color="white" class="ml-2">
            {{ lead.statusNazwa }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6">
              <div class="caption grey--text">Kontakt</div>
              <div>{{ lead.email || "—" }}</div>
              <div>{{ lead.telefon || "—" }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="caption grey--text">Sprawa</div>
              <div>
                {{ lead.procedura || "nie podano" }}
                <span v-if="lead.osob > 1">· {{ lead.osob }} osób</span>
              </div>
              <div class="caption">
                obywatelstwo:
                <span v-if="lead.obywatelstwoKod">{{ lead.obywatelstwoKod }}</span>
                <span v-else class="orange--text">nie ustalone — trzeba dopytać</span>
              </div>
            </v-col>
            <v-col v-if="lead.wiadomosc" cols="12">
              <div class="caption grey--text">Wiadomość</div>
              <div class="body-2">{{ lead.wiadomosc }}</div>
            </v-col>
            <v-col v-if="lead.ofertaBruttoGr" cols="12">
              <v-alert dense text :type="lead.ofertaWygasla ? 'warning' : 'info'" class="mb-0">
                Wysłana oferta: <strong>{{ zl(lead.ofertaBruttoGr) }}</strong>
                <span v-if="lead.osob > 1"> za osobę, razem {{ zl(lead.razemBruttoGr) }}</span>
                — {{ lead.ofertaWygasla ? "wygasła" : "ważna do" }} {{ formatDate(lead.ofertaWaznaDo) }}.
                <div class="caption">
                  Zmiana cennika nie rusza wysłanej oferty — klient zapłaci tę kwotę.
                </div>
              </v-alert>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="lead.status" :items="statusyLeada" item-text="nazwa" item-value="kod"
                label="Status" outlined dense hide-details
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-if="lead.status === 'PRZEGRANY'"
                v-model="lead.przegranyPowod" label="Dlaczego przegraliśmy?" outlined dense hide-details
              />
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="lead.note" label="Notatka" outlined dense hide-details rows="3" />
            </v-col>
          </v-row>
          <p class="caption grey--text mt-2 mb-0">
            Zgoda na warunki: wersja {{ lead.regulaminWersja || "—" }},
            {{ formatDate(lead.zgodaAt) }}<span v-if="lead.zgodaIp"> z adresu {{ lead.zgodaIp }}</span>.
            <span v-if="lead.zgodaMarketing">Zgoda marketingowa: tak.</span>
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogLeada = false">Zamknij</v-btn>
          <v-btn color="primary" :loading="zapisLeada" :disabled="!canEdit" @click="zapiszLeada">
            Zapisz
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Zapytanie → klient -->
    <v-dialog v-model="dialogKonwersji" max-width="560">
      <v-card v-if="konwersja.lead">
        <v-card-title class="subtitle-1">Zamień na klienta: {{ konwersja.lead.nazwa }}</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select v-model="konwersja.typ" :items="typyKlienta" item-text="nazwa" item-value="kod"
                label="Typ klienta" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="konwersja.nip" label="NIP (dla firmy)" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="konwersja.adres" label="Ulica i numer" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="2">
              <v-text-field v-model="konwersja.zip" label="Kod" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="konwersja.miasto" label="Miejscowość" outlined dense hide-details />
            </v-col>
          </v-row>
          <v-checkbox
            v-model="konwersja.utworzZamowienie"
            :disabled="!konwersja.lead.ofertaBruttoGr"
            label="Utwórz od razu zamówienie po cenie z oferty"
            dense hide-details class="mt-2"
          />
          <p v-if="konwersja.lead.ofertaBruttoGr" class="caption grey--text mt-2 mb-0">
            Zamówienie powstanie na {{ zl(konwersja.lead.ofertaBruttoGr) }}
            — kwotę z oferty, nie z aktualnego cennika.
          </p>
          <p v-else class="caption orange--text mt-2 mb-0">
            To zapytanie nie ma wyceny, więc zamówienia nie utworzymy — najpierw wyślij ofertę.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogKonwersji = false">Anuluj</v-btn>
          <v-btn color="success" :loading="zapisKonwersji" :disabled="!canEdit" @click="zamienNaKlienta">
            Zamień
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Partner -->
    <v-dialog v-model="dialogPartnera" max-width="560">
      <v-card>
        <v-card-title class="subtitle-1">
          {{ formPartnera.id ? "Edycja partnera" : "Nowy partner" }}
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="7">
              <v-text-field v-model="formPartnera.nazwa" label="Nazwa partnera" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="5">
              <v-text-field v-model="formPartnera.kod" label="Kod polecenia" outlined dense hide-details
                hint="Litery i cyfry, min. 3 znaki" persistent-hint />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="formPartnera.nagrodaZl" type="number" step="0.01"
                label="Nagroda za skuteczne polecenie (zł)" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="formPartnera.driverId" :items="rows" item-value="driverId"
                :item-text="(d) => d.name" label="Partner to kierowca BTP"
                outlined dense hide-details clearable :disabled="!!formPartnera.clientId"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="formPartnera.clientId" :items="klienciAktywni" item-text="nazwa" item-value="id"
                label="…albo klient z kartoteki" outlined dense hide-details clearable
                :disabled="!!formPartnera.driverId"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="formPartnera.email" label="E-mail" outlined dense hide-details />
            </v-col>
            <v-col cols="12">
              <v-switch v-model="formPartnera.aktywny" label="Partner aktywny (jego kod działa)"
                dense hide-details class="mt-0" />
            </v-col>
          </v-row>
          <p class="caption grey--text mt-2 mb-0">
            Kierowcy BTP dostają nagrodę jako dodatek do rozliczenia — od razu po zapłacie klienta.
            Pozostali trafiają na listę „do wypłaty".
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogPartnera = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="zapisPartnera" :disabled="!canEdit" @click="zapiszPartnera">
            Zapisz
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Abonament: dane -->
    <v-dialog v-model="dialogAbonamentu" max-width="640">
      <v-card>
        <v-card-title class="subtitle-1">
          {{ formAbonamentu.id ? 'Edycja abonamentu' : 'Nowy abonament' }}
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12">
              <v-text-field v-model="formAbonamentu.nazwa" label="Nazwa (widoczna na fakturze)"
                outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="formAbonamentu.clientId" :items="klienciAktywni"
                item-text="nazwa" item-value="id" label="Płaci firma / klient"
                outlined dense hide-details clearable
                :disabled="!!formAbonamentu.driverId"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="formAbonamentu.driverId" :items="rows" item-value="driverId"
                :item-text="(d) => d.name" label="…albo kierowca BTP"
                outlined dense hide-details clearable
                :disabled="!!formAbonamentu.clientId"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formAbonamentu.cenaOsobaZl" type="number" step="0.01"
                label="Cena za osobę / miesiąc (zł brutto)" outlined dense
                hint="Standardowo 49 zł" persistent-hint />
            </v-col>
            <v-col cols="12" md="4">
              <v-select v-model="formAbonamentu.vatRate" :items="[23, 8, 5, 0]" label="VAT %"
                outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model.number="formAbonamentu.dzienRozliczenia" type="number" min="1" max="28"
                label="Rozliczenie w dniu" outlined dense hide-details
                hint="1–28, bo lutego nie ma dłuższego" persistent-hint />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="formAbonamentu.startOd" type="date" label="Start abonamentu"
                outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="formAbonamentu.status" :items="statusyAbonamentu"
                item-text="nazwa" item-value="kod" label="Status" outlined dense hide-details />
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="formAbonamentu.note" label="Notatka" outlined dense hide-details rows="2" />
            </v-col>
          </v-row>
          <p class="caption grey--text mt-2 mb-0">
            Liczymy osoby objęte monitoringiem w dniu rozliczenia — nie dzielimy miesiąca na dni.
            Kto dojdzie później, wejdzie do kwoty w następnym miesiącu.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogAbonamentu = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="zapisAbonamentu"
            :disabled="!canEdit || !formAbonamentu.cenaOsobaZl" @click="zapiszAbonament">Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Abonament: karta z osobami i okresami -->
    <v-dialog v-model="dialogKartyAbonamentu" max-width="900" scrollable>
      <v-card v-if="kartaAb">
        <v-card-title class="subtitle-1">
          {{ kartaAb.nazwa }}
          <span class="caption grey--text ml-2">{{ kartaAb.nabywca }}</span>
          <v-spacer />
          <v-chip x-small :color="kolorAbonamentu(kartaAb.status)" text-color="white">
            {{ kartaAb.statusNazwa }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <div class="mb-3">
            {{ kartaAb.osobCzynnych }} osób × {{ zl(kartaAb.cenaOsobaGr) }} =
            <strong>{{ zl(kartaAb.miesiecznieBruttoGr) }}</strong> miesięcznie
            · rozliczenie {{ kartaAb.dzienRozliczenia }}. dnia
          </div>

          <div class="subtitle-2 mb-1">Osoby pod opieką</div>
          <v-simple-table dense>
            <tbody>
              <tr v-for="o in kartaAb.osoby" :key="o.id">
                <td>
                  {{ o.nazwa }}
                  <span v-if="o.driverId" class="caption grey--text">· kierowca BTP</span>
                </td>
                <td class="caption">od {{ formatDate(o.odDnia) }}</td>
                <td class="caption">
                  <span v-if="o.doDnia" class="grey--text">wypisana {{ formatDate(o.doDnia) }}</span>
                  <span v-else class="success--text">pod opieką</span>
                </td>
                <td class="text-right">
                  <v-btn v-if="!o.doDnia" icon x-small :disabled="!canEdit" @click="wypiszOsobe(o)">
                    <v-icon x-small>mdi-close</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-row dense align="center" class="mt-2">
            <v-col cols="12" md="5">
              <v-autocomplete
                v-model="nowaOsoba.driverId" :items="rows" item-value="driverId"
                :item-text="(d) => d.name" label="Kierowca BTP" outlined dense hide-details clearable
                :disabled="!!nowaOsoba.osoba"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="nowaOsoba.osoba" label="…albo imię i nazwisko z zewnątrz"
                outlined dense hide-details :disabled="!!nowaOsoba.driverId" />
            </v-col>
            <v-col cols="12" md="3">
              <v-btn small color="primary" :loading="zapisOsoby"
                :disabled="!canEdit || (!nowaOsoba.driverId && !nowaOsoba.osoba)" @click="dodajOsobe">
                <v-icon left small>mdi-account-plus</v-icon>Dodaj
              </v-btn>
            </v-col>
          </v-row>

          <v-divider class="my-4" />
          <div class="subtitle-2 mb-1">Rozliczone okresy</div>
          <p v-if="!kartaAb.okresy.length" class="caption grey--text mb-0">
            Żaden miesiąc nie był jeszcze rozliczony.
          </p>
          <v-simple-table v-else dense>
            <thead>
              <tr><th>Okres</th><th>Osób</th><th>Kwota</th><th>Status</th><th>Faktura</th></tr>
            </thead>
            <tbody>
              <tr v-for="o in kartaAb.okresy" :key="o.id">
                <td>{{ o.klucz }}</td>
                <td>{{ o.osob }}</td>
                <td>{{ zl(o.bruttoGr) }}</td>
                <td>
                  <v-chip x-small :color="kolorOkresu(o.status)" text-color="white">{{ o.status }}</v-chip>
                </td>
                <td class="caption">
                  <a v-if="o.faktura" @click.prevent="otworzPdf(o.faktura.id)">{{ o.faktura.number }}</a>
                  <span v-else class="grey--text">—</span>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
          <p class="caption grey--text mt-2 mb-0">
            Liczba osób i kwota w rozliczonym okresie są zamrożone — dopisanie albo wypisanie
            osoby zmienia dopiero następny miesiąc.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogKartyAbonamentu = false">Zamknij</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Klient: dane -->
    <v-dialog v-model="dialogKlienta" max-width="720">
      <v-card>
        <v-card-title class="subtitle-1">
          {{ formKlienta.id ? 'Edycja klienta' : 'Nowy klient' }}
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="8">
              <v-text-field v-model="formKlienta.nazwa" label="Nazwa firmy albo imię i nazwisko"
                outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-select v-model="formKlienta.typ" :items="typyKlienta" item-text="nazwa" item-value="kod"
                label="Typ" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formKlienta.nip" label="NIP" outlined dense hide-details
                :hint="formKlienta.typ === 'B2B' ? 'Potrzebny na fakturę dla firmy' : 'Osoba prywatna nie ma NIP-u'"
                persistent-hint />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formKlienta.regon" label="REGON (opcjonalnie)" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formKlienta.telefon" label="Telefon" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="formKlienta.adres" label="Ulica i numer" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="2">
              <v-text-field v-model="formKlienta.zip" label="Kod pocztowy" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="formKlienta.miasto" label="Miejscowość" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="formKlienta.email" label="E-mail (tam idą faktury)" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="formKlienta.osobaKontaktowa" label="Osoba do kontaktu" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model.number="formKlienta.rabatProcent" type="number" min="0" max="90"
                label="Rabat z umowy (%)" outlined dense hide-details
                hint="Puste = bez rabatu" persistent-hint />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model.number="formKlienta.terminPlatnosciDni" type="number" min="0" max="90"
                label="Termin płatności (dni)" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-select v-model="formKlienta.opiekunAdminId" :items="admini" item-text="nazwa" item-value="id"
                label="Opiekun" outlined dense hide-details clearable />
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="formKlienta.note" label="Notatka" outlined dense hide-details rows="2" />
            </v-col>
            <v-col cols="12">
              <v-switch v-model="formKlienta.aktywny" label="Klient aktywny (można mu sprzedawać)"
                dense hide-details class="mt-0" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogKlienta = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="zapisKlienta" :disabled="!canEdit || !formKlienta.nazwa"
            @click="zapiszKlienta">Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Klient: karta z cennikiem i zamówieniami -->
    <v-dialog v-model="dialogKarty" max-width="1000" scrollable>
      <v-card v-if="karta">
        <v-card-title class="subtitle-1">
          {{ karta.nazwa }}
          <span class="caption grey--text ml-2">
            {{ karta.typ === 'B2B' ? 'firma' : 'osoba prywatna' }}
            <span v-if="karta.nip">· NIP {{ karta.nip }}</span>
          </span>
          <v-spacer />
          <v-btn small color="primary" :disabled="!canEdit" @click="otworzZbiorcze(karta)">
            <v-icon left small>mdi-account-multiple-plus</v-icon>Zakup dla wielu osób
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row dense class="mb-2">
            <v-col cols="12" md="4">
              <div class="caption grey--text">Warunki</div>
              <div>
                <span v-if="karta.rabatProcent">rabat {{ karta.rabatProcent }}%</span>
                <span v-else class="grey--text">bez rabatu</span>
                · {{ karta.terminPlatnosciDni }} dni na zapłatę
              </div>
            </v-col>
            <v-col cols="12" md="8">
              <div class="caption grey--text">Cennik</div>
              <div>
                <span v-if="karta.maWlasnyCennik" class="success--text">własny cennik</span>
                <span v-else class="grey--text">ceny standardowe</span>
                <v-btn x-small text color="primary" :disabled="!canEdit"
                  class="ml-2" @click="przelaczCennik(karta)">
                  {{ karta.maWlasnyCennik ? 'wyłącz własny' : 'włącz własny' }}
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <template v-if="karta.maWlasnyCennik">
            <div class="subtitle-2 mt-3 mb-1">Ceny tego klienta</div>
            <p v-if="!karta.cennikWlasny.length" class="caption grey--text">
              Nie ma jeszcze żadnej ceny własnej — dopóki jej nie ustawisz, klient płaci
              ceny standardowe.
            </p>
            <v-simple-table v-else dense>
              <thead>
                <tr><th>Pakiet</th><th>Od ilu osób</th><th>Cena brutto</th><th>Od kiedy</th></tr>
              </thead>
              <tbody>
                <tr v-for="c in karta.cennikWlasny" :key="c.id">
                  <td>{{ c.pakiet.nazwa }}</td>
                  <td>{{ c.minIlosc }}</td>
                  <td class="font-weight-medium">{{ zl(c.bruttoGr) }}</td>
                  <td class="caption">{{ formatDate(c.obowiazujeOd) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <v-btn small text color="primary" :disabled="!canEdit" class="mt-1"
              @click="otworzCeneKlienta(karta)">
              <v-icon left small>mdi-plus</v-icon>Dodaj cenę albo próg
            </v-btn>
          </template>

          <v-divider class="my-4" />
          <div class="subtitle-2 mb-1">Zamówienia</div>
          <p v-if="!zamowieniaKlienta.length" class="caption grey--text mb-0">
            Ten klient nie ma jeszcze zamówień.
          </p>
          <template v-else>
            <div v-for="g in grupyKlienta" :key="g.klucz" class="mb-3">
              <div class="d-flex align-center">
                <div class="font-weight-medium">
                  <span v-if="g.grupaId">Paczka {{ g.pozycje.length }} osób</span>
                  <span v-else>Zamówienia pojedyncze</span>
                  <span class="caption grey--text ml-1">· {{ zl(g.razemGr) }}</span>
                </div>
                <v-spacer />
                <v-btn
                  v-if="g.grupaId && !g.zafakturowana"
                  x-small color="primary" outlined :disabled="!canEdit"
                  :loading="fakturaGrupy === g.grupaId"
                  @click="fakturaZbiorcza(g)"
                >Faktura zbiorcza na {{ g.pozycje.length }} osób</v-btn>
                <span v-else-if="g.grupaId" class="caption success--text">
                  faktura {{ g.numerFaktury }}
                </span>
              </div>
              <v-simple-table dense>
                <tbody>
                  <tr v-for="z in g.pozycje" :key="z.id">
                    <td>{{ z.osoba || '—' }}</td>
                    <td class="caption">{{ z.pakietNazwa }}</td>
                    <td>{{ zl(z.bruttoGr) }}</td>
                    <td>
                      <v-chip x-small :color="kolorPlatnosci(z.statusPlatnosci)" text-color="white">
                        {{ z.statusPlatnosciNazwa }}
                      </v-chip>
                    </td>
                    <td class="caption">
                      <a v-if="z.faktura" @click.prevent="otworzPdf(z.faktura.id)">{{ z.faktura.number }}</a>
                      <span v-else class="grey--text">bez faktury</span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogKarty = false">Zamknij</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Zakup dla wielu osób -->
    <v-dialog v-model="dialogZbiorczego" max-width="640">
      <v-card v-if="zbiorcze.klient">
        <v-card-title class="subtitle-1">Zakup dla wielu osób — {{ zbiorcze.klient.nazwa }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="zbiorcze.packageId" :items="pakietyDoSprzedazy" item-text="nazwa" item-value="id"
            label="Pakiet" outlined dense hide-details class="mb-3" @change="wycenZbiorcze"
          />
          <v-textarea
            v-model="zbiorcze.osobyTekst"
            label="Osoby — jedna na linię (imię i nazwisko)"
            outlined dense rows="6" hide-details
            @input="wycenZbiorcze"
          />
          <p class="caption grey--text mt-1">
            Nie zakładamy tym osobom kont w systemie — to pracownicy klienta, nie nasi
            kierowcy. Zapisujemy tylko, za kogo firma płaci.
          </p>
          <v-alert v-if="zbiorcze.wycena" type="info" text dense class="mt-3 mb-0">
            <div>
              {{ zbiorcze.osob }} osób × <strong>{{ zl(zbiorcze.wycena.doZaplatyBruttoGr) }}</strong>
              = <strong>{{ zl(zbiorcze.wycena.doZaplatyBruttoGr * zbiorcze.osob) }}</strong>
            </div>
            <div class="caption">
              cena katalogowa {{ zl(zbiorcze.wycena.pakiet.bruttoGr) }}
              <span v-if="zbiorcze.wycena.rabatKlientaGr">
                − rabat {{ zbiorcze.wycena.rabatProcent }}% ({{ zl(zbiorcze.wycena.rabatKlientaGr) }})
              </span>
              <span v-if="zbiorcze.wycena.pakiet.cennikUzyty === zbiorcze.klient.id">
                · z cennika tego klienta
              </span>
              <span v-else>· z cennika standardowego</span>
            </div>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogZbiorczego = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="zapisZbiorczego"
            :disabled="!canEdit || !zbiorcze.osob || !zbiorcze.packageId" @click="zamowZbiorczo">
            Zamów dla {{ zbiorcze.osob }} osób
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Faktura -->
    <v-dialog v-model="dialogFaktury" max-width="720" persistent>
      <v-card v-if="faktura.zamowienie">
        <v-card-title class="subtitle-1">
          Faktura za {{ faktura.zamowienie.pakietNazwa }}
        </v-card-title>
        <v-card-text>
          <v-alert v-if="faktura.braki && faktura.braki.length" type="warning" dense text class="mb-3">
            Kartoteka nie ma tych danych: <strong>{{ faktura.braki.join(', ') }}</strong>.
            Uzupełnij je tutaj — system nie wpisze ich za Ciebie.
          </v-alert>

          <v-row dense>
            <v-col cols="12" md="8">
              <v-text-field v-model="faktura.buyerName" label="Nabywca" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="faktura.buyerNip"
                label="NIP (puste = osoba fizyczna)"
                outlined dense hide-details
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="faktura.buyerAddress" label="Ulica i numer" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="2">
              <v-text-field v-model="faktura.buyerZip" label="Kod pocztowy" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="faktura.buyerCity" label="Miejscowość" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="8">
              <v-text-field v-model="faktura.buyerEmail" label="E-mail do wysyłki faktury" outlined dense hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="faktura.dniNaZaplate"
                type="number" min="0" max="90"
                label="Termin płatności (dni)"
                outlined dense hide-details
              />
            </v-col>
          </v-row>

          <div class="mt-4">
            <div class="subtitle-2 mb-1">Na fakturze</div>
            <v-simple-table dense>
              <tbody>
                <tr>
                  <td>Usługa legalizacji — {{ faktura.zamowienie.pakietNazwa }}</td>
                  <td class="text-right font-weight-medium">{{ zl(faktura.zamowienie.bruttoGr) }}</td>
                </tr>
                <tr v-for="k in kosztyBtp" :key="k.id">
                  <td>
                    <v-checkbox
                      v-model="k.naFakturze"
                      :label="k.nazwa + (k.szacunek ? ' (szacunek)' : '')"
                      dense hide-details class="mt-0 pt-0"
                      :disabled="!canEdit"
                      @change="zapiszKoszt(k)"
                    />
                    <v-select
                      v-if="k.naFakturze"
                      v-model="k.vatRate"
                      :items="[23, 8, 5, 0]"
                      label="VAT % dla tej pozycji"
                      outlined dense hide-details
                      style="max-width: 200px"
                      class="mt-1"
                      @change="zapiszKoszt(k)"
                    />
                  </td>
                  <td class="text-right">{{ zl(k.kwotaGr) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <p v-if="!kosztyBtp.length" class="caption grey--text mb-0 mt-1">
              Nie ma opłat wyłożonych przez nas do przeniesienia na fakturę.
            </p>
            <p v-if="oplatyKlienta" class="caption grey--text mb-0 mt-1">
              Opłaty urzędowe ({{ zl(oplatyKlienta) }}) klient wnosi sam — nie wchodzą na naszą fakturę.
            </p>
            <div class="mt-2 text-right subtitle-1 font-weight-bold">
              Razem: {{ zl(sumaFaktury) }}
            </div>
          </div>

          <v-checkbox
            v-model="faktura.wyslijMailem"
            :disabled="!faktura.buyerEmail"
            label="Wyślij fakturę mailem od razu po wystawieniu"
            dense hide-details class="mt-3"
          />
          <p class="caption grey--text mt-2 mb-0">
            Faktura dostanie numer w osobnej serii (FV/LEG/…). Do KSeF nie poleci sama —
            wyślesz ją osobnym przyciskiem, gdy sprawdzisz dane.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogFaktury = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="zapisFaktury" :disabled="!canEdit" @click="wystawFakture">
            Wystaw fakturę
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Pakiet -->
    <v-dialog v-model="dialogPakietu" max-width="640">
      <v-card>
        <v-card-title class="subtitle-1">{{ pakiet.id ? 'Edycja pakietu' : 'Nowy pakiet' }}</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="4">
              <v-text-field v-model="pakiet.kod" label="Kod (np. STANDARD)" outlined dense hide-details />
            </v-col>
            <v-col cols="8">
              <v-text-field v-model="pakiet.nazwa" label="Nazwa widoczna dla klienta" outlined dense hide-details />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="pakiet.opis" label="Krótki opis" outlined dense hide-details />
            </v-col>
            <v-col cols="4">
              <v-select v-model="pakiet.vatRate" :items="[23, 8, 5, 0]" label="VAT %" outlined dense hide-details />
            </v-col>
            <v-col cols="4">
              <v-text-field v-model.number="pakiet.kolejnosc" type="number" label="Kolejność" outlined dense hide-details />
            </v-col>
            <v-col cols="4" class="d-flex align-center">
              <v-switch v-model="pakiet.aktywny" label="Aktywny" hide-details class="mt-0" />
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="pakiet.procedury" :items="procedury" item-text="nazwa" item-value="kod"
                label="Dotyczy procedur (puste = wszystkich)" outlined dense hide-details multiple chips small-chips
              />
            </v-col>
          </v-row>

          <div class="section-divider mt-3">Co wchodzi w pakiet</div>
          <div v-for="(z, i) in pakiet.zakres" :key="i" class="d-flex align-center mb-1">
            <v-text-field v-model="pakiet.zakres[i]" outlined dense hide-details />
            <v-btn icon small class="ml-1" @click="pakiet.zakres.splice(i, 1)">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-btn x-small text color="primary" @click="pakiet.zakres.push('')">
            <v-icon left x-small>mdi-plus</v-icon>Dodaj pozycję
          </v-btn>
          <p class="caption grey--text mt-2 mb-0">
            Ta lista jest tym, co klient przeczyta przed zakupem. Nie obiecuj tu decyzji urzędu.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogPakietu = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="zapisPakietu" @click="zapiszPakiet">Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cena -->
    <v-dialog v-model="dialogCeny" max-width="480">
      <v-card v-if="cena.pakiet">
        <v-card-title class="subtitle-1">
          Cena: {{ cena.pakiet.nazwa }}
          <span class="caption grey--text ml-2">{{ opisCennika(cena.cennik) }}</span>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="cena.bruttoZl" type="number" label="Cena brutto (zł)"
            outlined dense hide-details autofocus
          />
          <p class="caption grey--text mt-2">
            Podaj kwotę, którą zapłaci klient. Netto i VAT policzymy z niej sami —
            dzięki temu „999 zł" jest dokładnie 999 zł, a nie 999,01.
          </p>
          <v-text-field
            v-model.number="cena.minIlosc" type="number" min="1"
            label="Obowiązuje od tylu osób (próg ilościowy)"
            outlined dense hide-details class="mb-2 mt-3"
          />
          <p class="caption grey--text mb-2">
            1 = cena zwykła. Wpisz 5, żeby ta kwota obowiązywała przy zakupie dla pięciu
            i więcej osób — progi dla mniejszych liczb zostają bez zmian.
          </p>
          <v-text-field v-model="cena.obowiazujeOd" type="date" label="Obowiązuje od" outlined dense hide-details class="mb-2" />
          <v-text-field v-model="cena.komentarz" label="Powód zmiany (do historii)" outlined dense hide-details />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogCeny = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="zapisCeny" :disabled="!cena.bruttoZl" @click="zapiszCene">Zapisz cenę</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Zapłata -->
    <v-dialog v-model="dialogZaplaty" max-width="480">
      <v-card v-if="zaplata.zamowienie">
        <v-card-title class="subtitle-1">Oznacz zapłatę</v-card-title>
        <v-card-text>
          <div class="mb-3">
            <div class="font-weight-medium">{{ zaplata.zamowienie.pakietNazwa }}</div>
            <div class="caption grey--text">
              do zapłaty na rzecz BTP: {{ zl(zaplata.zamowienie.podsumowanie.doZaplatyBtpGr) }}
            </div>
          </div>
          <v-select
            v-model="zaplata.sposobPlatnosci" :items="sposobyPlatnosci"
            item-text="nazwa" item-value="kod" label="Sposób zapłaty" outlined dense hide-details
          />
          <p v-if="zaplata.sposobPlatnosci === 'POTRACENIE'" class="caption mt-2 mb-0">
            Kwota wejdzie jako potrącenie w najbliższym rozliczeniu kierowcy — tak samo
            jak opłata za rozliczenie czy wypisy.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogZaplaty = false">Anuluj</v-btn>
          <v-btn color="success" :loading="zapisZaplaty" :disabled="!zaplata.sposobPlatnosci"
            @click="zapiszZaplate">Potwierdź</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Ustalenie obywatelstwa bez wchodzenia w kartę -->
    <v-dialog v-model="dialogKraju" max-width="440">
      <v-card v-if="wybrany">
        <v-card-title class="subtitle-1">Obywatelstwo — {{ wybrany.name }}</v-card-title>
        <v-card-text>
          <p class="caption grey--text">
            Przy rejestracji wpisano: „{{ wybrany.citizenshipText || 'nic' }}".
            Ustal na podstawie dokumentu, nie domysłu.
          </p>
          <v-autocomplete
            v-model="krajDoZapisu"
            :items="kraje"
            item-text="namePl"
            item-value="code"
            label="Kraj"
            outlined dense hide-details autofocus
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="zapisywanie" @click="dialogKraju = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="zapisywanie" :disabled="!krajDoZapisu" @click="zapiszKraj(krajDoZapisu)">
            Zapisz
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Kreator nowej sprawy -->
    <v-dialog v-model="dialogKreatora" max-width="640" persistent>
      <v-card>
        <v-card-title class="subtitle-1">Nowa sprawa legalizacyjna</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="nowa.driverId"
            :items="kandydaci"
            item-text="name"
            item-value="driverId"
            label="Kierowca"
            outlined dense class="mb-3" hide-details
          />
          <v-select
            v-model="nowa.procedura"
            :items="procedury"
            item-text="nazwa"
            item-value="kod"
            label="Rodzaj sprawy"
            outlined dense class="mb-3" hide-details
          />
          <v-select
            v-model="nowa.wojewodztwo"
            :items="wojewodztwa"
            label="Województwo / urząd prowadzący"
            outlined dense class="mb-3" hide-details clearable
          />
          <div class="section-divider">Podstawa pracy</div>
          <v-radio-group v-model="nowa.pracodawcaBTP" class="mt-1" hide-details>
            <v-radio label="BTP zatrudnia tę osobę (umowa, zlecenie)" :value="true" />
            <v-radio label="Kierowca prowadzi własną działalność" :value="false" />
            <v-radio label="Jeszcze nie wiem — niech system o to zapyta" :value="null" />
          </v-radio-group>
          <p class="caption grey--text mt-3 mb-0">
            Od tego zależy, które dokumenty system dobierze. Jeśli nie wiesz, zostaw
            ostatnią opcję — na checkliście pojawi się to jako pytanie do rozstrzygnięcia.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="tworzenie" @click="dialogKreatora = false">Anuluj</v-btn>
          <v-btn
            color="primary"
            :loading="tworzenie"
            :disabled="!nowa.driverId || !nowa.procedura"
            @click="utworzSprawe"
          >Utwórz sprawę</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <legalization-case-dialog
      v-model="dialogSprawy"
      :case-id="otwartaSprawa"
      :can-edit="canEdit"
      @zmieniono="pobierzSprawy"
    />

    <p class="caption grey--text mt-3 mb-8">
      Zestawienie liczone z dat wpisanych w kartach kierowców i z zatwierdzonych reguł.
      System nie orzeka o legalności pobytu ani zatrudnienia — ocenia to pracownik.
    </p>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import LegalizationCaseDialog from "@/components/LegalizationCaseDialog.vue";
import { canEditSubtab } from "@/services/permissions";

export default {
  name: "LegalizationPage",
  components: { LegalizationCaseDialog },
  data() {
    return {
      zakladka: 0,
      loading: false,
      rows: [],
      summary: {},
      search: "",
      citizenship: null,
      status: null,
      kraje: [],
      wojewodztwa: [],
      dialogKraju: false,
      wybrany: null,
      krajDoZapisu: null,
      zapisywanie: false,

      // sprawy
      sprawy: [],
      ladowanieSpraw: false,
      szukajSprawy: "",
      filtrStatusSprawy: null,
      filtrProcedura: null,
      procedury: [],
      statusySprawy: [],
      dialogSprawy: false,
      otwartaSprawa: null,
      dialogKreatora: false,
      tworzenie: false,
      nowa: { driverId: null, procedura: "POBYT_I_PRACA", wojewodztwo: null, pracodawcaBTP: null },

      // reguły
      zestawy: [],
      ladowanieRegul: false,
      wgrywanie: false,

      // sprzedaż
      pakiety: [],
      zamowienia: [],
      raport: {},
      ladowanieSprzedazy: false,
      filtrPlatnosci: null,
      statusyPlatnosci: [],
      sposobyPlatnosci: [],
      dialogPakietu: false,
      dialogCeny: false,
      dialogZaplaty: false,
      zapisPakietu: false,
      zapisCeny: false,
      zapisZaplaty: false,
      pakiet: { zakres: [] },
      cena: { pakiet: null },
      zaplata: { zamowienie: null },
      // klienci zewnętrzni
      klienci: [],
      ladowanieKlientow: false,
      szukajKlienta: '',
      typyKlienta: [],
      admini: [],
      dialogKlienta: false,
      zapisKlienta: false,
      formKlienta: {},
      dialogKarty: false,
      karta: null,
      zamowieniaKlienta: [],
      dialogZbiorczego: false,
      zapisZbiorczego: false,
      zbiorcze: { klient: null, packageId: null, osobyTekst: '', osob: 0, wycena: null },
      fakturaGrupy: null,
      naglowkiKlientow: [
        { text: 'Klient', value: 'nazwa' },
        { text: 'Kontakt', value: 'kontakt', sortable: false },
        { text: 'Warunki', value: 'warunki', sortable: false },
        { text: 'Sprzedaż', value: 'sprzedaz', sortable: false },
        { text: 'Opiekun', value: 'opiekun', sortable: false },
        { text: '', value: 'akcje', sortable: false, align: 'end' },
      ],

      // zapytania i polecenia
      leady: [],
      raportLeadow: {},
      partnerzy: [],
      nagrody: [],
      ladowanieLeadow: false,
      filtrLeada: null,
      szukajLeada: '',
      statusyLeada: [],
      dialogLeada: false,
      zapisLeada: false,
      lead: null,
      ofertaLeada: null,
      dialogKonwersji: false,
      zapisKonwersji: false,
      konwersja: { lead: null },
      dialogPartnera: false,
      zapisPartnera: false,
      formPartnera: {},
      naglowkiLeadow: [
        { text: 'Kto pyta', value: 'nazwa' },
        { text: 'Sprawa', value: 'sprawa', sortable: false },
        { text: 'Oferta', value: 'oferta', sortable: false },
        { text: 'Status', value: 'status' },
        { text: '', value: 'akcje', sortable: false, align: 'end' },
      ],

      // abonamenty
      abonamenty: [],
      okresy: [],
      raportAb: {},
      ladowanieAbonamentow: false,
      filtrOkresu: null,
      statusyAbonamentu: [],
      statusyOkresu: [],
      dialogAbonamentu: false,
      zapisAbonamentu: false,
      formAbonamentu: {},
      dialogKartyAbonamentu: false,
      kartaAb: null,
      nowaOsoba: { driverId: null, osoba: '' },
      zapisOsoby: false,
      domykanie: null,
      fakturaOkresu: null,
      naglowkiAbonamentow: [
        { text: 'Abonament', value: 'nazwa' },
        { text: 'Status', value: 'status' },
        { text: 'Osób', value: 'osoby', sortable: false },
        { text: 'Miesięcznie', value: 'miesiecznie', sortable: false },
        { text: 'Rozliczenie', value: 'rozliczenie', sortable: false },
        { text: '', value: 'akcje', sortable: false, align: 'end' },
      ],
      naglowkiOkresow: [
        { text: 'Okres', value: 'klucz' },
        { text: 'Nabywca', value: 'nabywca' },
        { text: 'Osób', value: 'osob', sortable: false },
        { text: 'Kwota', value: 'bruttoGr' },
        { text: 'Status', value: 'status' },
        { text: 'Faktura', value: 'faktura', sortable: false },
        { text: '', value: 'akcje', sortable: false, align: 'end' },
      ],

      dialogFaktury: false,
      zapisFaktury: false,
      faktura: { zamowienie: null },
      kosztyBtp: [],
      wysylka: null,
      ksefTrwa: null,
      naglowkiZamowien: [
        { text: 'Klient', value: 'klient' },
        { text: 'Pakiet', value: 'pakietNazwa' },
        { text: 'Kwota', value: 'bruttoGr' },
        { text: 'Płatność', value: 'statusPlatnosciNazwa' },
        { text: 'Faktura', value: 'faktura', sortable: false },
        { text: 'Utworzono', value: 'createdAt' },
        { text: '', value: 'akcje', sortable: false, align: 'end' },
      ],

      headers: [
        { text: "Kierowca", value: "name" },
        { text: "Obywatelstwo", value: "citizenshipName" },
        { text: "Stan", value: "status" },
        { text: "Pobyt do", value: "residenceUntil" },
        { text: "Praca do", value: "workUntil" },
        { text: "Braki", value: "braki", sortable: false },
        { text: "Dane sprawdzone", value: "verifiedAt", sortable: false },
        { text: "", value: "akcje", sortable: false, align: "end" },
      ],
      naglowkiSpraw: [
        { text: "Kierowca", value: "driverName" },
        { text: "Rodzaj sprawy", value: "proceduraNazwa" },
        { text: "Status", value: "statusNazwa" },
        { text: "Kompletność", value: "kompletnosc", sortable: false },
        { text: "Utworzona", value: "createdAt" },
        { text: "Termin", value: "deadlineAt" },
      ],
      naglowkiRegul: [
        { text: "Zestaw", value: "nazwa" },
        { text: "Stan", value: "status" },
        { text: "Sprawdzono", value: "zweryfikowanoDnia" },
        { text: "Źródło", value: "zrodloUrl", sortable: false },
        { text: "", value: "akcje", sortable: false, align: "end" },
      ],
      kafelki: [
        { status: "PO_TERMINIE", pole: "poTerminie", tytul: "Po terminie", ikona: "mdi-alert-octagon", kolor: "error" },
        { status: "KONCZY_SIE", pole: "konczySie", tytul: "Kończy się (90 dni)", ikona: "mdi-clock-alert-outline", kolor: "orange" },
        { status: "WYMAGA_DANYCH", pole: "wymagaDanych", tytul: "Brakuje danych", ikona: "mdi-help-circle-outline", kolor: "blue-grey" },
        { status: "AKTUALNY", pole: "aktualni", tytul: "Daty aktualne", ikona: "mdi-check-circle-outline", kolor: "success" },
      ],
      opcjeStatusow: [
        { text: "Po terminie", value: "PO_TERMINIE" },
        { text: "Kończy się", value: "KONCZY_SIE" },
        { text: "Brakuje danych", value: "WYMAGA_DANYCH" },
        { text: "Daty aktualne", value: "AKTUALNY" },
      ],
    };
  },
  computed: {
    nagrodyDoWyplaty() {
      return this.nagrody.filter((n) => n.status === 'DO_WYPLATY');
    },
    klienciAktywni() {
      return this.klienci.filter((k) => k.aktywny);
    },
    pakietyDoSprzedazy() {
      return this.pakiety.filter((p) => p.aktywny !== false);
    },
    /**
     * Zamówienia klienta pogrupowane w paczki. Paczka to jeden zakup dla wielu
     * osób i idzie na jedną fakturę; zamówienia bez paczki zostają razem na dole.
     */
    grupyKlienta() {
      const paczki = new Map();
      const pojedyncze = [];
      for (const z of this.zamowieniaKlienta) {
        if (z.grupaId) {
          if (!paczki.has(z.grupaId)) paczki.set(z.grupaId, []);
          paczki.get(z.grupaId).push(z);
        } else {
          pojedyncze.push(z);
        }
      }
      const grupy = [...paczki.entries()].map(([grupaId, pozycje]) => ({
        klucz: grupaId,
        grupaId,
        pozycje,
        razemGr: pozycje.reduce((sum, z) => sum + z.bruttoGr, 0),
        zafakturowana: pozycje.every((z) => !!z.invoiceId),
        numerFaktury: (pozycje.find((z) => z.faktura) || {}).faktura?.number || null,
      }));
      if (pojedyncze.length) {
        grupy.push({
          klucz: 'pojedyncze',
          grupaId: null,
          pozycje: pojedyncze,
          razemGr: pojedyncze.reduce((sum, z) => sum + z.bruttoGr, 0),
          zafakturowana: pojedyncze.every((z) => !!z.invoiceId),
          numerFaktury: null,
        });
      }
      return grupy;
    },
    /** Tyle wyjdzie na fakturze — usługa plus zaznaczone do refakturowania. */
    sumaFaktury() {
      if (!this.faktura.zamowienie) return 0;
      return this.kosztyBtp
        .filter((k) => k.naFakturze)
        .reduce((s, k) => s + k.kwotaGr, this.faktura.zamowienie.bruttoGr);
    },
    oplatyKlienta() {
      if (!this.faktura.zamowienie) return 0;
      return (this.faktura.zamowienie.kosztyZewnetrzne || [])
        .filter((k) => k.ktoPlaci === 'KLIENT')
        .reduce((s, k) => s + k.kwotaGr, 0);
    },
    canEdit() {
      return canEditSubtab("legalization", "cases");
    },
    opcjeObywatelstw() {
      const nazwy = [...new Set(this.rows.map((r) => r.citizenshipName).filter(Boolean))];
      return nazwy.sort((a, b) => a.localeCompare(b, "pl"));
    },
    widoczne() {
      const szukane = (this.search || "").toLowerCase().trim();
      return this.rows.filter((r) => {
        if (this.status && r.status !== this.status) return false;
        if (this.citizenship && r.citizenshipName !== this.citizenship) return false;
        if (szukane && !r.name.toLowerCase().includes(szukane)) return false;
        return true;
      });
    },
    widoczneSprawy() {
      const szukane = (this.szukajSprawy || "").toLowerCase().trim();
      return this.sprawy.filter((s) => {
        if (this.filtrStatusSprawy && s.status !== this.filtrStatusSprawy) return false;
        if (this.filtrProcedura && s.procedura !== this.filtrProcedura) return false;
        if (szukane && !s.driverName.toLowerCase().includes(szukane)) return false;
        return true;
      });
    },
    /** Do kreatora podpowiadamy tych, których moduł faktycznie dotyczy. */
    kandydaci() {
      return this.rows.map((r) => ({
        driverId: r.driverId,
        name: `${r.name}${r.citizenshipName ? " — " + r.citizenshipName : ""}`,
      }));
    },
  },
  watch: {
    zakladka(v) {
      if (v === 1 && !this.sprawy.length) this.pobierzSprawy();
      if (v === 2 && !this.zestawy.length) this.pobierzReguly();
      if (v === 3) this.pobierzSprzedaz();
      if (v === 4) this.pobierzKlientow();
      if (v === 5) this.pobierzAbonamenty();
      if (v === 6) this.pobierzLeady();
    },
  },
  mounted() {
    this.fetch();
    this.pobierzSlowniki();
  },
  methods: {
    getConfig() {
      return { headers: { Authorization: "Bearer " + localStorage.getItem("token") } };
    },
    odswiez() {
      if (this.zakladka === 0) this.fetch();
      else if (this.zakladka === 1) this.pobierzSprawy();
      else if (this.zakladka === 2) this.pobierzReguly();
      else if (this.zakladka === 3) this.pobierzSprzedaz();
      else if (this.zakladka === 4) this.pobierzKlientow();
      else if (this.zakladka === 5) this.pobierzAbonamenty();
      else this.pobierzLeady();
    },
    async fetch() {
      this.loading = true;
      try {
        const res = await axios.get(
          process.env.VUE_APP_ROOT_API + "legalization/overview",
          this.getConfig()
        );
        this.rows = res.data.data.rows || [];
        this.summary = res.data.data.summary || {};
      } catch (e) {
        this.blad(e, "Nie udało się pobrać zestawienia");
      } finally {
        this.loading = false;
      }
    },
    async pobierzSlowniki() {
      try {
        const [sl, sprawy] = await Promise.all([
          axios.get(process.env.VUE_APP_ROOT_API + "legalization/dictionaries", this.getConfig()),
          axios.get(process.env.VUE_APP_ROOT_API + "legalization/case-dictionaries", this.getConfig()),
        ]);
        this.kraje = sl.data.data.countries || [];
        this.wojewodztwa = sl.data.data.voivodeships || [];
        this.procedury = sprawy.data.data.procedury || [];
        this.statusySprawy = sprawy.data.data.statusySprawy || [];
      } catch (e) {
        // Brak słowników nie może wywalić całej strony — filtry po prostu będą puste.
      }
    },
    async pobierzSprawy() {
      this.ladowanieSpraw = true;
      try {
        const res = await axios.get(
          process.env.VUE_APP_ROOT_API + "legalization/cases",
          this.getConfig()
        );
        this.sprawy = res.data.data || [];
      } catch (e) {
        this.blad(e, "Nie udało się pobrać spraw");
      } finally {
        this.ladowanieSpraw = false;
      }
    },
    async pobierzReguly() {
      this.ladowanieRegul = true;
      try {
        const res = await axios.get(
          process.env.VUE_APP_ROOT_API + "legalization/rules",
          this.getConfig()
        );
        this.zestawy = res.data.data || [];
      } catch (e) {
        this.blad(e, "Nie udało się pobrać reguł");
      } finally {
        this.ladowanieRegul = false;
      }
    },
    zl(gr) {
      if (gr === null || gr === undefined) return '—';
      return (gr / 100).toFixed(2).replace('.', ',') + ' zł';
    },
    cenaZ(pakiet, cennik) {
      // Bierzemy najświeższą obowiązującą cenę z danego cennika.
      const teraz = Date.now();
      const pasujace = (pakiet.ceny || [])
        .filter((c) => c.cennik === cennik && !c.deleted)
        .filter((c) => !c.obowiazujeDo || new Date(c.obowiazujeDo).getTime() >= teraz)
        .filter((c) => !c.obowiazujeOd || new Date(c.obowiazujeOd).getTime() <= teraz);
      if (!pasujace.length) return null;
      return pasujace.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0].bruttoGr;
    },
    async pobierzSprzedaz() {
      this.ladowanieSprzedazy = true;
      try {
        const [pak, sl, rap] = await Promise.all([
          axios.get(process.env.VUE_APP_ROOT_API + 'legalization/packages', this.getConfig()),
          axios.get(process.env.VUE_APP_ROOT_API + 'legalization/sales-dictionaries', this.getConfig()),
          axios.get(process.env.VUE_APP_ROOT_API + 'legalization/orders/report', this.getConfig()),
        ]);
        this.pakiety = pak.data.data || [];
        this.statusyPlatnosci = sl.data.data.statusyPlatnosci || [];
        this.sposobyPlatnosci = sl.data.data.sposobyPlatnosci || [];
        this.raport = rap.data.data || {};
        await this.pobierzZamowienia();
      } catch (e) {
        this.blad(e, 'Nie udało się pobrać danych sprzedaży');
      } finally {
        this.ladowanieSprzedazy = false;
      }
    },
    async pobierzZamowienia() {
      try {
        const res = await axios.get(
          process.env.VUE_APP_ROOT_API + 'legalization/orders',
          { ...this.getConfig(), params: { statusPlatnosci: this.filtrPlatnosci || undefined } }
        );
        this.zamowienia = res.data.data || [];
      } catch (e) {
        this.blad(e, 'Nie udało się pobrać zamówień');
      }
    },
    otworzPakiet(p) {
      this.pakiet = p
        ? { ...p, zakres: [...(p.zakres || [])], procedury: [...(p.procedury || [])] }
        : { kod: '', nazwa: '', opis: '', vatRate: 23, kolejnosc: 0, aktywny: true, zakres: [], procedury: [] };
      this.dialogPakietu = true;
    },
    async zapiszPakiet() {
      this.zapisPakietu = true;
      try {
        await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/packages',
          { ...this.pakiet, zakres: this.pakiet.zakres.filter((z) => z && z.trim()) },
          this.getConfig()
        );
        this.dialogPakietu = false;
        await this.pobierzSprzedaz();
      } catch (e) {
        this.blad(e, 'Nie zapisano pakietu');
      } finally {
        this.zapisPakietu = false;
      }
    },
    otworzCene(pakiet, cennik) {
      const obecna = this.cenaZ(pakiet, cennik);
      this.cena = {
        pakiet, cennik,
        bruttoZl: obecna ? (obecna / 100).toFixed(2) : '',
        minIlosc: 1,
        obowiazujeOd: new Date().toISOString().slice(0, 10),
        komentarz: '',
      };
      this.dialogCeny = true;
    },
    async zapiszCene() {
      this.zapisCeny = true;
      try {
        const zl = parseFloat(String(this.cena.bruttoZl).replace(',', '.'));
        await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/packages/price',
          {
            packageId: this.cena.pakiet.id,
            cennik: this.cena.cennik,
            // Administrator myśli w złotych, baza trzyma grosze.
            bruttoGr: Math.round(zl * 100),
            minIlosc: this.cena.minIlosc || 1,
            obowiazujeOd: this.cena.obowiazujeOd,
            komentarz: this.cena.komentarz,
          },
          this.getConfig()
        );
        this.dialogCeny = false;
        if (this.karta && this.cena.cennik === this.karta.id) await this.otworzKarte(this.karta);
        else await this.pobierzSprzedaz();
      } catch (e) {
        this.blad(e, 'Nie zapisano ceny');
      } finally {
        this.zapisCeny = false;
      }
    },
    otworzZaplate(zamowienie) {
      this.zaplata = {
        zamowienie,
        sposobPlatnosci: zamowienie.driver ? 'POTRACENIE' : 'PRZELEW',
      };
      this.dialogZaplaty = true;
    },
    async zapiszZaplate() {
      this.zapisZaplaty = true;
      try {
        await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/orders/' + this.zaplata.zamowienie.id + '/paid',
          { sposobPlatnosci: this.zaplata.sposobPlatnosci },
          this.getConfig()
        );
        this.dialogZaplaty = false;
        await this.pobierzSprzedaz();
      } catch (e) {
        this.blad(e, 'Nie oznaczono zapłaty');
      } finally {
        this.zapisZaplaty = false;
      }
    },
    kolorLeada(status) {
      if (status === 'WYGRANY') return 'success';
      if (status === 'AKCEPTACJA') return 'teal';
      if (status === 'OFERTA_WYSLANA') return 'blue-grey';
      if (status === 'PRZEGRANY') return 'error';
      if (status === 'SPAM') return 'grey';
      return 'orange';
    },
    pobierzLeadyZOpoznieniem() {
      clearTimeout(this._szukajLeada);
      this._szukajLeada = setTimeout(() => this.pobierzLeady(), 350);
    },
    async pobierzLeady() {
      this.ladowanieLeadow = true;
      try {
        const [ld, rap, part, nagr, sl] = await Promise.all([
          axios.get(process.env.VUE_APP_ROOT_API + 'legalization/leads', {
            ...this.getConfig(),
            params: { status: this.filtrLeada || undefined, szukaj: this.szukajLeada || undefined },
          }),
          axios.get(process.env.VUE_APP_ROOT_API + 'legalization/leads/report', this.getConfig()),
          axios.get(process.env.VUE_APP_ROOT_API + 'legalization/referrers', this.getConfig()),
          axios.get(process.env.VUE_APP_ROOT_API + 'legalization/rewards', this.getConfig()),
          axios.get(process.env.VUE_APP_ROOT_API + 'legalization/sales-dictionaries', this.getConfig()),
        ]);
        this.leady = ld.data.data || [];
        this.raportLeadow = rap.data.data || {};
        this.partnerzy = part.data.data || [];
        this.nagrody = nagr.data.data || [];
        this.statusyLeada = sl.data.data.statusyLeada || [];
        this.typyKlienta = sl.data.data.typyKlienta || [];
        // Kierowcy i klienci są potrzebni w dialogu partnera.
        if (!this.rows.length) await this.fetch();
        if (!this.klienci.length) await this.pobierzKlientow();
      } catch (e) {
        this.blad(e, 'Nie udało się pobrać zapytań');
      } finally {
        this.ladowanieLeadow = false;
      }
    },
    otworzLeada(l) {
      this.lead = { ...l };
      this.dialogLeada = true;
    },
    async zapiszLeada() {
      this.zapisLeada = true;
      try {
        await axios.patch(
          process.env.VUE_APP_ROOT_API + 'legalization/leads/' + this.lead.id,
          {
            status: this.lead.status,
            note: this.lead.note,
            przegranyPowod: this.lead.przegranyPowod,
          },
          this.getConfig()
        );
        this.dialogLeada = false;
        await this.pobierzLeady();
      } catch (e) {
        this.blad(e, 'Nie zapisano zapytania');
      } finally {
        this.zapisLeada = false;
      }
    },
    async wyslijOferte(l) {
      this.ofertaLeada = l.id;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/leads/' + l.id + '/offer',
          {},
          this.getConfig()
        );
        const d = res.data.data;
        Swal.fire(
          d.wyslana ? 'Oferta wysłana' : 'Oferta policzona',
          `${d.pakiet}: ${this.zl(d.bruttoGr)}, ważna do ` +
            new Date(d.wazneDo).toLocaleDateString('pl-PL') +
            (d.wyslana ? '. Klient dostał ją mailem.' : '. Mailem nie poszła — brak adresu albo błąd poczty.'),
          d.wyslana ? 'success' : 'warning'
        );
        await this.pobierzLeady();
      } catch (e) {
        this.blad(e, 'Nie wysłano oferty');
      } finally {
        this.ofertaLeada = null;
      }
    },
    otworzKonwersje(l) {
      this.konwersja = {
        lead: l,
        typ: 'B2C',
        nip: '',
        adres: '',
        zip: '',
        miasto: '',
        utworzZamowienie: !!l.ofertaBruttoGr,
      };
      this.dialogKonwersji = true;
    },
    async zamienNaKlienta() {
      this.zapisKonwersji = true;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/leads/' + this.konwersja.lead.id + '/convert',
          {
            typ: this.konwersja.typ,
            nip: this.konwersja.nip || undefined,
            adres: this.konwersja.adres || undefined,
            zip: this.konwersja.zip || undefined,
            miasto: this.konwersja.miasto || undefined,
            utworzZamowienie: this.konwersja.utworzZamowienie,
          },
          this.getConfig()
        );
        const d = res.data.data;
        this.dialogKonwersji = false;
        Swal.fire(
          'Klient założony',
          d.zamowienie
            ? `Powstało zamówienie na ${this.zl(d.zamowienie.bruttoGr)} — znajdziesz je w zakładce Sprzedaż.`
            : 'Kartoteka gotowa. Zamówienie wystawisz z zakładki Sprzedaż.',
          'success'
        );
        await this.pobierzLeady();
      } catch (e) {
        this.blad(e, 'Nie udało się zamienić na klienta');
      } finally {
        this.zapisKonwersji = false;
      }
    },
    otworzPartnera(p) {
      this.formPartnera = p
        ? { ...p, nagrodaZl: (p.nagrodaGr / 100).toFixed(2) }
        : { nazwa: '', kod: '', nagrodaZl: '', driverId: null, clientId: null, email: '', aktywny: true };
      this.dialogPartnera = true;
    },
    async zapiszPartnera() {
      this.zapisPartnera = true;
      try {
        const zlKwota = parseFloat(String(this.formPartnera.nagrodaZl).replace(',', '.'));
        await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/referrers',
          { ...this.formPartnera, nagrodaGr: Math.round(zlKwota * 100) },
          this.getConfig()
        );
        this.dialogPartnera = false;
        await this.pobierzLeady();
      } catch (e) {
        this.blad(e, 'Nie zapisano partnera');
      } finally {
        this.zapisPartnera = false;
      }
    },
    async nagrodaWyplacona(n) {
      try {
        await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/rewards/' + n.id + '/paid',
          {},
          this.getConfig()
        );
        await this.pobierzLeady();
      } catch (e) {
        this.blad(e, 'Nie oznaczono nagrody');
      }
    },
    kolorAbonamentu(status) {
      if (status === 'AKTYWNY') return 'success';
      if (status === 'WSTRZYMANY') return 'orange';
      return 'grey';
    },
    kolorOkresu(status) {
      if (status === 'OPLACONY') return 'success';
      if (status === 'ZAFAKTUROWANY') return 'blue-grey';
      if (status === 'ANULOWANY') return 'grey';
      return 'orange';
    },
    async pobierzAbonamenty() {
      this.ladowanieAbonamentow = true;
      try {
        const [ab, rap, sl] = await Promise.all([
          axios.get(process.env.VUE_APP_ROOT_API + 'legalization/subscriptions', this.getConfig()),
          axios.get(process.env.VUE_APP_ROOT_API + 'legalization/subscriptions/report', this.getConfig()),
          axios.get(process.env.VUE_APP_ROOT_API + 'legalization/sales-dictionaries', this.getConfig()),
        ]);
        this.abonamenty = ab.data.data || [];
        this.raportAb = rap.data.data || {};
        this.statusyAbonamentu = sl.data.data.statusyAbonamentu || [];
        this.statusyOkresu = sl.data.data.statusyOkresu || [];
        await this.pobierzOkresy();
        // Lista klientów i kierowców jest potrzebna w dialogach abonamentu.
        if (!this.klienci.length) await this.pobierzKlientow();
        if (!this.rows.length) await this.fetch();
      } catch (e) {
        this.blad(e, 'Nie udało się pobrać abonamentów');
      } finally {
        this.ladowanieAbonamentow = false;
      }
    },
    async pobierzOkresy() {
      try {
        const res = await axios.get(process.env.VUE_APP_ROOT_API + 'legalization/subscriptions/periods', {
          ...this.getConfig(),
          params: { status: this.filtrOkresu || undefined },
        });
        this.okresy = res.data.data || [];
      } catch (e) {
        this.blad(e, 'Nie udało się pobrać okresów');
      }
    },
    otworzAbonament(a) {
      this.formAbonamentu = a
        ? {
            ...a,
            cenaOsobaZl: (a.cenaOsobaGr / 100).toFixed(2),
            startOd: String(a.startOd).slice(0, 10),
          }
        : {
            nazwa: 'Monitoring terminów', clientId: null, driverId: null,
            // Stawka zatwierdzona przez właściciela 27.09.2026 — podpowiedź,
            // nie przymus: przy większej firmie można zejść niżej.
            cenaOsobaZl: '49.00', vatRate: 23, dzienRozliczenia: 1,
            startOd: new Date().toISOString().slice(0, 10), status: 'AKTYWNY', note: '',
          };
      this.dialogAbonamentu = true;
    },
    async zapiszAbonament() {
      this.zapisAbonamentu = true;
      try {
        const zlKwota = parseFloat(String(this.formAbonamentu.cenaOsobaZl).replace(',', '.'));
        await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/subscriptions',
          {
            ...this.formAbonamentu,
            // Administrator myśli w złotych, baza trzyma grosze.
            cenaOsobaGr: Math.round(zlKwota * 100),
          },
          this.getConfig()
        );
        this.dialogAbonamentu = false;
        await this.pobierzAbonamenty();
      } catch (e) {
        this.blad(e, 'Nie zapisano abonamentu');
      } finally {
        this.zapisAbonamentu = false;
      }
    },
    async otworzKarteAbonamentu(a) {
      try {
        const res = await axios.get(
          process.env.VUE_APP_ROOT_API + 'legalization/subscriptions/' + a.id, this.getConfig());
        this.kartaAb = res.data.data;
        this.nowaOsoba = { driverId: null, osoba: '' };
        this.dialogKartyAbonamentu = true;
        if (!this.rows.length) await this.fetch();
      } catch (e) {
        this.blad(e, 'Nie udało się otworzyć abonamentu');
      }
    },
    async dodajOsobe() {
      this.zapisOsoby = true;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/subscriptions/' + this.kartaAb.id + '/people',
          { driverId: this.nowaOsoba.driverId || undefined, osoba: this.nowaOsoba.osoba || undefined },
          this.getConfig()
        );
        this.kartaAb = res.data.data;
        this.nowaOsoba = { driverId: null, osoba: '' };
        await this.pobierzAbonamenty();
      } catch (e) {
        this.blad(e, 'Nie dodano osoby');
      } finally {
        this.zapisOsoby = false;
      }
    },
    async wypiszOsobe(osoba) {
      const potw = await Swal.fire({
        title: 'Wypisać ' + osoba.nazwa + ' z monitoringu?',
        text: 'Rozliczone miesiące zostają bez zmian — zmieni się dopiero następne rozliczenie.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Wypisuję',
        cancelButtonText: 'Nie',
      });
      if (!potw.isConfirmed) return;
      try {
        const res = await axios.delete(
          process.env.VUE_APP_ROOT_API + 'legalization/subscriptions/people/' + osoba.id,
          this.getConfig()
        );
        this.kartaAb = res.data.data;
        await this.pobierzAbonamenty();
      } catch (e) {
        this.blad(e, 'Nie wypisano osoby');
      }
    },
    async domknijOkres(a) {
      const potw = await Swal.fire({
        title: 'Rozliczyć miesiąc dla „' + a.nazwa + '"?',
        text:
          `Policzymy ${a.osobCzynnych} osób × ${this.zl(a.cenaOsobaGr)} = ` +
          `${this.zl(a.miesiecznieBruttoGr)}. Ta liczba osób zostanie zamrożona — ` +
          'późniejsze zmiany nie ruszą tego okresu.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Rozliczam',
        cancelButtonText: 'Nie teraz',
      });
      if (!potw.isConfirmed) return;

      this.domykanie = a.id;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/subscriptions/' + a.id + '/close',
          {},
          this.getConfig()
        );
        const o = res.data.data;
        Swal.fire('Okres ' + o.klucz, `${o.osob} osób, ${this.zl(o.bruttoGr)} — czeka na fakturę.`, 'success');
        await this.pobierzAbonamenty();
      } catch (e) {
        this.blad(e, 'Nie rozliczono miesiąca');
      } finally {
        this.domykanie = null;
      }
    },
    async fakturaZaOkres(okres) {
      this.fakturaOkresu = okres.id;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/subscriptions/periods/' + okres.id + '/invoice',
          { wyslijMailem: true },
          this.getConfig()
        );
        const d = res.data.data;
        const ostrz = (d.ostrzezenia || []).join(' ');
        Swal.fire(
          'Faktura ' + d.faktura.number,
          `Okres ${d.okres}, ${d.osob} osób, ${this.zl(d.faktura.bruttoTotal)}. ` +
            (d.wysylka && d.wysylka.poszlo ? 'Wysłana mailem. ' : '') + ostrz,
          ostrz ? 'warning' : 'success'
        );
        await this.pobierzAbonamenty();
      } catch (e) {
        this.blad(e, 'Nie wystawiono faktury za okres');
      } finally {
        this.fakturaOkresu = null;
      }
    },
    opisCennika(kod) {
      if (kod === 'KIEROWCA_BTP') return 'dla kierowców BTP';
      if (kod === 'STANDARD' || !kod) return 'standardowa';
      const klient = this.klienci.find((k) => k.id === kod) || this.karta;
      return klient && klient.id === kod ? `dla klienta: ${klient.nazwa}` : 'cennik klienta';
    },
    pobierzKlientowZOpoznieniem() {
      clearTimeout(this._szukajKlienta);
      this._szukajKlienta = setTimeout(() => this.pobierzKlientow(), 350);
    },
    async pobierzKlientow() {
      this.ladowanieKlientow = true;
      try {
        const [kl, sl] = await Promise.all([
          axios.get(process.env.VUE_APP_ROOT_API + 'legalization/clients', {
            ...this.getConfig(),
            params: { szukaj: this.szukajKlienta || undefined },
          }),
          axios.get(process.env.VUE_APP_ROOT_API + 'legalization/sales-dictionaries', this.getConfig()),
        ]);
        this.klienci = kl.data.data || [];
        this.typyKlienta = sl.data.data.typyKlienta || [];
        this.statusyPlatnosci = sl.data.data.statusyPlatnosci || [];
        if (!this.pakiety.length) {
          const pak = await axios.get(
            process.env.VUE_APP_ROOT_API + 'legalization/packages', this.getConfig());
          this.pakiety = pak.data.data || [];
        }
      } catch (e) {
        this.blad(e, 'Nie udało się pobrać klientów');
      } finally {
        this.ladowanieKlientow = false;
      }
    },
    async pobierzAdminow() {
      if (this.admini.length) return;
      try {
        const res = await axios.get(process.env.VUE_APP_ROOT_API + 'company-admins', this.getConfig());
        const lista = res.data.data || res.data || [];
        this.admini = (Array.isArray(lista) ? lista : lista.rows || []).map((a) => ({
          id: a.id,
          nazwa: `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email,
        }));
      } catch (e) {
        // Lista opiekunów to wygoda, nie warunek pracy — brak nie blokuje zapisu.
        this.admini = [];
      }
    },
    otworzKlienta(k) {
      this.formKlienta = k
        ? { ...k }
        : {
            nazwa: '', typ: 'B2B', nip: '', regon: '', adres: '', zip: '', miasto: '',
            email: '', telefon: '', osobaKontaktowa: '', rabatProcent: null,
            terminPlatnosciDni: 7, opiekunAdminId: null, aktywny: true, note: '',
          };
      this.dialogKlienta = true;
      this.pobierzAdminow();
    },
    async zapiszKlienta() {
      this.zapisKlienta = true;
      try {
        await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/clients',
          this.formKlienta,
          this.getConfig()
        );
        this.dialogKlienta = false;
        await this.pobierzKlientow();
        if (this.karta && this.karta.id === this.formKlienta.id) await this.otworzKarte(this.karta);
      } catch (e) {
        this.blad(e, 'Nie zapisano klienta');
      } finally {
        this.zapisKlienta = false;
      }
    },
    async wylaczKlienta(k) {
      const potw = await Swal.fire({
        title: 'Wyłączyć klienta ' + k.nazwa + '?',
        text: 'Zamówienia i faktury zostają — klient zniknie tylko z listy i nie będzie można mu sprzedawać.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Wyłączam',
        cancelButtonText: 'Nie',
      });
      if (!potw.isConfirmed) return;
      try {
        await axios.delete(
          process.env.VUE_APP_ROOT_API + 'legalization/clients/' + k.id, this.getConfig());
        await this.pobierzKlientow();
      } catch (e) {
        this.blad(e, 'Nie wyłączono klienta');
      }
    },
    async otworzKarte(k) {
      try {
        const [karta, zam] = await Promise.all([
          axios.get(process.env.VUE_APP_ROOT_API + 'legalization/clients/' + k.id, this.getConfig()),
          axios.get(process.env.VUE_APP_ROOT_API + 'legalization/orders', {
            ...this.getConfig(), params: { clientId: k.id },
          }),
        ]);
        this.karta = karta.data.data;
        this.zamowieniaKlienta = zam.data.data || [];
        this.dialogKarty = true;
      } catch (e) {
        this.blad(e, 'Nie udało się otworzyć kartoteki klienta');
      }
    },
    async przelaczCennik(klient) {
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/clients/' + klient.id + '/pricelist',
          { wlacz: !klient.maWlasnyCennik },
          this.getConfig()
        );
        this.karta = res.data.data;
        await this.pobierzKlientow();
      } catch (e) {
        this.blad(e, 'Nie zmieniono cennika');
      }
    },
    otworzCeneKlienta(klient) {
      // Ten sam dialog co przy cenniku standardowym — cennik klienta to po
      // prostu cennik o kodzie równym jego id.
      const pakiet = this.pakietyDoSprzedazy[0];
      if (!pakiet) {
        Swal.fire('Najpierw pakiet', 'Nie ma żadnego pakietu, dla którego ustawić cenę.', 'info');
        return;
      }
      this.otworzCene(pakiet, klient.id);
    },
    otworzZbiorcze(klient) {
      this.zbiorcze = {
        klient,
        packageId: (this.pakietyDoSprzedazy[0] || {}).id || null,
        osobyTekst: '',
        osob: 0,
        wycena: null,
      };
      this.dialogZbiorczego = true;
    },
    osobyZTekstu() {
      return (this.zbiorcze.osobyTekst || '')
        .split('\n')
        .map((o) => o.trim())
        .filter(Boolean);
    },
    async wycenZbiorcze() {
      const osoby = this.osobyZTekstu();
      this.zbiorcze.osob = osoby.length;
      if (!osoby.length || !this.zbiorcze.packageId) {
        this.zbiorcze.wycena = null;
        return;
      }
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/orders/quote',
          {
            packageId: this.zbiorcze.packageId,
            clientId: this.zbiorcze.klient.id,
            ilosc: osoby.length,
          },
          this.getConfig()
        );
        this.zbiorcze.wycena = res.data.data;
      } catch (e) {
        this.zbiorcze.wycena = null;
        this.blad(e, 'Nie udało się wycenić');
      }
    },
    async zamowZbiorczo() {
      this.zapisZbiorczego = true;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/orders/bulk',
          {
            packageId: this.zbiorcze.packageId,
            clientId: this.zbiorcze.klient.id,
            osoby: this.osobyZTekstu(),
            procedura: 'POBYT_I_PRACA',
          },
          this.getConfig()
        );
        const d = res.data.data;
        this.dialogZbiorczego = false;
        Swal.fire(
          'Zamówione dla ' + d.osob + ' osób',
          this.zl(d.cenaJednostkowaBruttoGr) + ' za osobę, razem ' + this.zl(d.razemBruttoGr) +
            '. Teraz możesz wystawić jedną fakturę na całą paczkę.',
          'success'
        );
        await this.otworzKarte(this.zbiorcze.klient);
        await this.pobierzKlientow();
      } catch (e) {
        this.blad(e, 'Nie udało się zamówić');
      } finally {
        this.zapisZbiorczego = false;
      }
    },
    async fakturaZbiorcza(grupa) {
      this.fakturaGrupy = grupa.grupaId;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/orders/bulk/' + grupa.grupaId + '/invoice',
          { wyslijMailem: !!(this.karta && this.karta.email) },
          this.getConfig()
        );
        const d = res.data.data;
        const ostrz = (d.ostrzezenia || []).join(' ');
        Swal.fire(
          'Faktura ' + d.faktura.number,
          `${d.zamowien} pozycji na ${this.zl(d.faktura.bruttoTotal)}. ` +
            (d.wysylka && d.wysylka.poszlo ? 'Wysłana mailem. ' : '') + ostrz,
          ostrz ? 'warning' : 'success'
        );
        await this.otworzKarte(this.karta);
      } catch (e) {
        this.blad(e, 'Nie wystawiono faktury zbiorczej');
      } finally {
        this.fakturaGrupy = null;
      }
    },
    async otworzFakture(zamowienie) {
      this.faktura = { zamowienie, dniNaZaplate: 7, wyslijMailem: false, braki: [] };
      this.kosztyBtp = (zamowienie.kosztyZewnetrzne || [])
        .filter((k) => k.ktoPlaci === 'BTP')
        .map((k) => ({ ...k }));
      this.dialogFaktury = true;
      try {
        const res = await axios.get(
          process.env.VUE_APP_ROOT_API + 'legalization/orders/' + zamowienie.id + '/buyer',
          this.getConfig()
        );
        const d = res.data.data || {};
        // Podstawiamy to, co system wie; braków nie wymyślamy — pokazujemy je wprost.
        this.faktura = {
          ...this.faktura,
          buyerName: d.buyerName || '',
          buyerNip: d.buyerNip || '',
          buyerAddress: d.buyerAddress || '',
          buyerZip: d.buyerZip || '',
          buyerCity: d.buyerCity || '',
          buyerEmail: d.buyerEmail || '',
          wyslijMailem: !!d.buyerEmail,
          braki: d.braki || [],
        };
      } catch (e) {
        this.blad(e, 'Nie udało się pobrać danych nabywcy');
      }
    },
    async zapiszKoszt(koszt) {
      try {
        await axios.patch(
          process.env.VUE_APP_ROOT_API + 'legalization/costs/' + koszt.id + '/invoiceable',
          { naFakturze: !!koszt.naFakturze, vatRate: koszt.naFakturze ? (koszt.vatRate || 23) : null },
          this.getConfig()
        );
        if (koszt.naFakturze && !koszt.vatRate) koszt.vatRate = 23;
      } catch (e) {
        // Cofamy zaznaczenie, żeby ekran nie pokazywał czegoś, czego baza nie przyjęła.
        koszt.naFakturze = !koszt.naFakturze;
        this.blad(e, 'Nie zapisano pozycji faktury');
      }
    },
    async wystawFakture() {
      this.zapisFaktury = true;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/orders/' + this.faktura.zamowienie.id + '/invoice',
          {
            buyerName: this.faktura.buyerName,
            buyerNip: this.faktura.buyerNip || null,
            buyerAddress: this.faktura.buyerAddress || null,
            buyerZip: this.faktura.buyerZip || null,
            buyerCity: this.faktura.buyerCity || null,
            buyerEmail: this.faktura.buyerEmail || null,
            dniNaZaplate: this.faktura.dniNaZaplate,
            wyslijMailem: this.faktura.wyslijMailem,
          },
          this.getConfig()
        );
        const d = res.data.data;
        this.dialogFaktury = false;
        const ostrz = (d.ostrzezenia || []).join(' ');
        Swal.fire(
          'Faktura ' + d.faktura.number,
          (d.wysylka && d.wysylka.poszlo ? 'Wysłana mailem. ' : '') + ostrz,
          ostrz ? 'warning' : 'success'
        );
        await this.pobierzSprzedaz();
      } catch (e) {
        this.blad(e, 'Nie wystawiono faktury');
      } finally {
        this.zapisFaktury = false;
      }
    },
    async otworzPdf(invoiceId) {
      try {
        const res = await axios.get(
          process.env.VUE_APP_ROOT_API + 'legalization/invoices/' + invoiceId + '/pdf',
          { ...this.getConfig(), responseType: 'blob' }
        );
        window.open(URL.createObjectURL(res.data), '_blank');
      } catch (e) {
        this.blad(e, 'Nie udało się otworzyć faktury');
      }
    },
    async wyslijFakture(faktura, ponownie) {
      this.wysylka = faktura.id;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/invoices/' + faktura.id + '/email',
          { ponownie: !!ponownie },
          this.getConfig()
        );
        const d = res.data.data;
        Swal.fire(
          d.poszlo ? 'Wysłano' : 'Nie wysłano',
          d.poszlo ? 'Faktura poszła na ' + faktura.buyerEmail : String(d.blad || ''),
          d.poszlo ? 'success' : 'error'
        );
        await this.pobierzSprzedaz();
      } catch (e) {
        this.blad(e, 'Nie wysłano faktury');
      } finally {
        this.wysylka = null;
      }
    },
    async doKsef(faktura) {
      // Wysyłki do KSeF nie da się cofnąć — pytamy wprost, zamiast wysyłać po kliknięciu.
      const potw = await Swal.fire({
        title: 'Wysłać ' + faktura.number + ' do KSeF?',
        text:
          'Faktura trafi do Krajowego Systemu e-Faktur. Po przyjęciu nie można jej wycofać — ' +
          'tylko skorygować. Sprawdź dane nabywcy przed wysyłką.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Wysyłam',
        cancelButtonText: 'Jeszcze nie',
      });
      if (!potw.isConfirmed) return;

      this.ksefTrwa = faktura.id;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/invoices/' + faktura.id + '/ksef',
          {},
          this.getConfig()
        );
        const d = res.data.data;
        Swal.fire(
          d.ksefStatus === 'SENT' ? 'Przyjęta przez KSeF' : 'Wysłana, czeka na numer',
          d.ksefNumber ? 'Numer KSeF: ' + d.ksefNumber : 'Status: ' + d.ksefStatus,
          d.ksefStatus === 'ERROR' ? 'error' : 'success'
        );
        await this.pobierzSprzedaz();
      } catch (e) {
        this.blad(e, 'KSeF nie przyjął faktury');
      } finally {
        this.ksefTrwa = null;
      }
    },
    async fakturaZaplacona(faktura) {
      try {
        await axios.post(
          process.env.VUE_APP_ROOT_API + 'legalization/invoices/' + faktura.id + '/paid',
          {},
          this.getConfig()
        );
        if (this.zakladka === 5) await this.pobierzAbonamenty();
        else await this.pobierzSprzedaz();
      } catch (e) {
        this.blad(e, 'Nie oznaczono faktury jako zapłaconej');
      }
    },
    kolorPlatnosci(status) {
      if (['OPLACONE', 'FAKTURA_OPLACONA'].includes(status)) return 'success';
      if (['ANULOWANE', 'ZWROT'].includes(status)) return 'grey';
      if (status === 'CZESCIOWO') return 'orange';
      return 'blue-grey';
    },
    otworzKreator() {
      this.nowa = { driverId: null, procedura: "POBYT_I_PRACA", wojewodztwo: null, pracodawcaBTP: null };
      this.dialogKreatora = true;
    },
    async utworzSprawe() {
      this.tworzenie = true;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + "legalization/cases",
          this.nowa,
          this.getConfig()
        );
        this.dialogKreatora = false;
        await this.pobierzSprawy();
        this.otworzSprawe(res.data.data.id);
      } catch (e) {
        this.blad(e, "Nie udało się utworzyć sprawy");
      } finally {
        this.tworzenie = false;
      }
    },
    otworzSprawe(id) {
      this.otwartaSprawa = id;
      this.dialogSprawy = true;
    },
    async wgrajStartowe() {
      const { isConfirmed } = await Swal.fire({
        icon: "question",
        title: "Wgrać zestawy startowe?",
        text:
          "Zestawy wejdą jako wersje robocze. Zanim system zacznie z nich liczyć, " +
          "trzeba sprawdzić źródło każdego z nich i zatwierdzić.",
        showCancelButton: true,
        confirmButtonText: "Wgraj",
        cancelButtonText: "Anuluj",
      });
      if (!isConfirmed) return;
      this.wgrywanie = true;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + "legalization/rules/seed",
          {},
          this.getConfig()
        );
        await this.pobierzReguly();
        Swal.fire("Gotowe", `Dodano zestawów: ${res.data.data.dodane.length}. ${res.data.data.uwaga}`, "success");
      } catch (e) {
        this.blad(e, "Nie udało się wgrać zestawów");
      } finally {
        this.wgrywanie = false;
      }
    },
    async zatwierdzZestaw(zestaw) {
      const { isConfirmed, value } = await Swal.fire({
        icon: "warning",
        title: "Zatwierdzić zestaw reguł?",
        html:
          "Od tej chwili system będzie z niego liczył wymagane dokumenty.<br>" +
          "Potwierdzasz, że sprawdziłeś źródło: <b>" +
          (zestaw.zrodloUrl || "brak") +
          "</b>",
        input: "text",
        inputPlaceholder: "Komentarz (opcjonalnie)",
        showCancelButton: true,
        confirmButtonText: "Zatwierdzam",
        cancelButtonText: "Anuluj",
      });
      if (!isConfirmed) return;
      try {
        await axios.post(
          process.env.VUE_APP_ROOT_API + "legalization/rules/" + zestaw.id + "/activate",
          { komentarz: value },
          this.getConfig()
        );
        await this.pobierzReguly();
      } catch (e) {
        this.blad(e, "Nie udało się zatwierdzić");
      }
    },
    async archiwizujZestaw(zestaw) {
      const { isConfirmed } = await Swal.fire({
        icon: "question",
        title: "Zarchiwizować zestaw?",
        text: "System przestanie z niego liczyć checklisty. Istniejące sprawy zostaną bez zmian do czasu przeliczenia.",
        showCancelButton: true,
        confirmButtonText: "Archiwizuj",
        cancelButtonText: "Anuluj",
      });
      if (!isConfirmed) return;
      try {
        await axios.post(
          process.env.VUE_APP_ROOT_API + "legalization/rules/" + zestaw.id + "/archive",
          {},
          this.getConfig()
        );
        await this.pobierzReguly();
      } catch (e) {
        this.blad(e, "Nie udało się zarchiwizować");
      }
    },
    toggleStatus(status) {
      this.status = this.status === status ? null : status;
    },
    wyczyscFiltry() {
      this.search = "";
      this.citizenship = null;
      this.status = null;
    },
    otworzKierowce(item) {
      // Tak samo jak z pozostałych list w panelu — pełne przeładowanie karty.
      window.location.href = "/driver-details?id=" + item.driverId;
    },
    async otworzUstalanie(item) {
      this.wybrany = item;
      this.krajDoZapisu = null;
      if (!this.kraje.length) await this.pobierzSlowniki();
      this.dialogKraju = true;
    },
    szybkoPolska(item) {
      this.wybrany = item;
      this.zapiszKraj("PL");
    },
    async zapiszKraj(kod) {
      this.zapisywanie = true;
      try {
        await axios.patch(
          process.env.VUE_APP_ROOT_API + "legalization/driver/" + this.wybrany.driverId + "/citizenship",
          { citizenship: kod },
          this.getConfig()
        );
        this.dialogKraju = false;
        await this.fetch();
      } catch (e) {
        this.blad(e, "Nie zapisano obywatelstwa");
      } finally {
        this.zapisywanie = false;
      }
    },
    blad(e, tytul) {
      Swal.fire(tytul, (e.response && e.response.data && e.response.data.message) || "", "error");
    },
    formatDate(v) {
      if (!v) return "—";
      return new Date(v).toLocaleDateString("pl-PL");
    },
    opisDni(dni) {
      if (dni === null || dni === undefined) return "—";
      if (dni < 0) return `${Math.abs(dni)} dni po terminie`;
      if (dni === 0) return "dziś";
      return `za ${dni} dni`;
    },
    klasaDni(dni) {
      if (dni === null || dni === undefined) return "grey--text";
      if (dni <= 30) return "error--text";
      if (dni <= 90) return "orange--text";
      return "grey--text";
    },
    kolorStanu(status) {
      return { PO_TERMINIE: "error", KONCZY_SIE: "orange", WYMAGA_DANYCH: "blue-grey", AKTUALNY: "success" }[status] || "grey";
    },
    etykietaStanu(status) {
      return {
        PO_TERMINIE: "Po terminie",
        KONCZY_SIE: "Kończy się",
        WYMAGA_DANYCH: "Brakuje danych",
        AKTUALNY: "Daty aktualne",
      }[status] || status;
    },
    kolorSprawy(status) {
      if (["DECYZJA_NEGATYWNA", "WEZWANIE"].includes(status)) return "error";
      if (["ZWERYFIKOWANA", "DECYZJA_POZYTYWNA", "ZAKONCZONA"].includes(status)) return "success";
      if (["ZLOZONA", "OCZEKIWANIE_NA_URZAD"].includes(status)) return "blue";
      return "blue-grey";
    },
    kolorZestawu(status) {
      return { ACTIVE: "success", DRAFT: "orange", ARCHIVED: "grey" }[status] || "grey";
    },
    etykietaZestawu(status) {
      return { ACTIVE: "Zatwierdzony", DRAFT: "Roboczy", ARCHIVED: "Archiwalny" }[status] || status;
    },
  },
};
</script>

<style scoped>
.legalization-page {
  padding: 8px 4px 40px;
}
.page-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}
.page-eyebrow {
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8f98;
}
.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}
.lg-tile {
  cursor: pointer;
  transition: border-color 0.15s ease;
}
.lg-tile--active {
  border-color: #1976d2;
  border-width: 2px;
}
.section-divider {
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a8f98;
  margin: 12px 0 4px;
}
</style>
